import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { PageTitle } from "@/components/PageHeader";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { useDash, fileUrl } from "./ctx";
import { EXPIRE_TIMES, expiryPreview } from "./messages/time";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VoiceNote } from "@/components/VoiceNote";
import { ImportQA } from "@/components/ImportQA";
import { DictationMic } from "@/components/DictationMic";
import { TextAssist, RephraseStyleMenu } from "@/components/TextAssist";
import { usePaged, ShowMore } from "@/components/Paged";
import { InfoHint } from "@/components/InfoHint";
import DocumentsLibrary from "./DocumentsLibrary";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  FileText, Trash2, UploadCloud, FileStack, Eye, Landmark, ShieldCheck, Scale,
  CalendarDays, Megaphone, Info, Phone, Plus, Sparkles, Clock, Loader2,
  Lightbulb, Check, X, MessagesSquare, Search, RotateCcw, ChevronDown, Pencil,
  LayoutGrid, List, AlertCircle, ArrowUpDown, Download, Hourglass, Tag, UserCircle2, Lock,
  Link2, Wand2, AlertTriangle, RefreshCw, Scissors, Paperclip, Archive, ArrowRightLeft,
} from "lucide-react";

const kindsFactory = () => ({
  announcement: { label: "Announcement", icon: Megaphone, color: "text-amber-600", bg: "bg-amber-50", hint: "Gate down, temporary notice…" },
  fact: { label: "Fact / Info", icon: Info, color: "text-sky-600", bg: "bg-sky-50", hint: `Quiet hours, pet rules, ${V.lcs("dues")}…` },
});

// Sticky in-page jump nav — lets managers hop directly to Documents or Quick knowledge
// without hunting through the page as the library grows. Sticks under the top bar so it
// stays visible once you scroll past the H1.
function _JumpChip({ id, label, count, testid, onJump }) {
  return (
    <a href={`#${id}`} data-testid={testid} onClick={onJump(id)}
       className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-3.5 py-1.5 text-sm font-semibold text-ink-700 hover:border-ink hover:text-ink transition-colors">
      {label}
      {typeof count === "number" && count > 0 && (
        <span className="text-[11px] font-bold text-ink-600 bg-sand-100 rounded-full px-1.5">{count}</span>
      )}
    </a>
  );
}
function SectionNav({ counts }) {
  const jump = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <nav data-testid="doc-section-nav" className="sticky top-2 z-30 -mt-4 mb-2 flex items-center gap-2 flex-wrap rounded-full bg-white/70 backdrop-blur border border-sand-200 px-2 py-1.5 shadow-sm w-fit">
      <span className="text-[11px] font-bold uppercase tracking-wide text-ink-500 pl-2 pr-1">Jump to</span>
      <_JumpChip id="add-knowledge" label="Add knowledge" testid="jump-add" onJump={jump} />
      <_JumpChip id="documents" label="Documents" count={counts?.docs} testid="jump-documents" onJump={jump} />
      <_JumpChip id="quick-knowledge" label="Quick knowledge" count={counts?.quick} testid="jump-quick" onJump={jump} />
      {counts?.expiring > 0 && <span data-testid="nav-expiring" className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-[11px] font-bold">{counts.expiring} expiring soon</span>}
    </nav>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    // Handles BOTH date-only strings ("2026-02-04") and full timestamps (created_at).
    const d = new Date(String(iso).includes("T") ? iso : iso + "T00:00:00");
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
function dateParts(iso) {
  try {
    const d = new Date(String(iso).includes("T") ? iso : iso + "T00:00:00");
    if (isNaN(d.getTime())) return { mon: "", day: "" };
    return { mon: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(), day: d.getDate() };
  } catch { return { mon: "", day: "" }; }
}


const EMPTY_FORM = { id: null, kind: "announcement", title: "", description: "", date: "", time: "", expire_time: "", tags: [], audiences: ["anyone"], require_verification: false, expires_at: "", expiry_action: "archive" };

// Per-item audience: who the AI may share this with (enforced at retrieval — a restricted
// item never enters the AI's context for an ineligible caller). Shared with VoiceNote.
import { AudiencePicker } from "@/components/AudiencePicker";
import { useAudiences, chipsOf, isOpen, describeChips, ALL_PEOPLE } from "@/lib/audiences";
import { LibraryPicker } from "@/components/knowledge/LibraryPicker";
import { CommunityChip, CommunityBanner, ExpiryBadge, ExpiryField, ExpirySuggestionsStrip, ArchivePanel, MoveToCommunityDialog, shortName, looksLikeNewVersion, toastAdded } from "@/components/knowledge/Expiry";
import { V } from "@/lib/vocab";

// Small pill shown on cards/rows and in the filter bar.
function TagChip({ t, onClick, active }) {
  const Comp = onClick ? "button" : "span";
  return (
    <Comp data-testid={`tag-chip-${t}`} onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${
        active ? "bg-ink text-white" : "bg-sand-100 text-ink/60"}${onClick ? " hover:bg-ink hover:text-white cursor-pointer" : ""}`}>
      <Tag className="h-2.5 w-2.5" /> {t}
    </Comp>
  );
}

// Tag editor for the entry dialog: type + Enter/comma to add, backspace to pop, X to remove.
function TagsField({ tags, onChange, onSuggest, suggesting, readOnly }) {
  const [draft, setDraft] = useState("");
  const add = (raw) => {
    const t = raw.trim().replace(/^#/, "").slice(0, 30);
    if (t && !tags.some((x) => x.toLowerCase() === t.toLowerCase()) && tags.length < 8) onChange([...tags, t]);
    setDraft("");
  };
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-semibold">Tags <span className="text-xs font-normal text-muted-foreground">(optional — the words {V.lcs("person")} actually say, e.g. pool, swim times)</span></label>
        {onSuggest && !readOnly && (
          <button type="button" data-testid="entry-tags-suggest" onClick={onSuggest} disabled={suggesting || tags.length >= 8}
            title={tags.length >= 8 ? "Tag limit reached (8) — remove one to add AI suggestions" : `Let the AI suggest tags ${V.lcs("person")} would say`}
            className="flex items-center gap-1 rounded-full bg-redline-50 text-redline-600 border border-redline-200 px-2.5 py-1 text-xs font-bold hover:bg-redline-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {suggesting ? <div className="h-3 w-3 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /> : <Sparkles className="h-3 w-3" strokeWidth={2} />}
            {suggesting ? "Thinking…" : "Suggest tags"}
          </button>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 rounded-xl border border-sand-200 px-3 py-2 focus-within:ring-2 focus-within:ring-redline-500/30">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-sand-100 text-ink/70 px-2.5 py-1 text-xs font-semibold">
            <Tag className="h-3 w-3" /> {t}
            {!readOnly && <button data-testid={`entry-tag-remove-${t}`} onClick={() => onChange(tags.filter((x) => x !== t))} className="ml-0.5 text-ink/40 hover:text-redline-600"><X className="h-3 w-3" /></button>}
          </span>
        ))}
        {readOnly ? (tags.length ? null : <span className="text-xs text-ink/40">No tags</span>) : (
        <input data-testid="entry-tags-input" value={draft}
          onChange={(e) => e.target.value.includes(",") ? add(e.target.value.replace(",", "")) : setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(draft); }
            else if (e.key === "Backspace" && !draft && tags.length) onChange(tags.slice(0, -1));
          }}
          onBlur={() => draft.trim() && add(draft)}
          placeholder={tags.length ? "" : "Type a tag, press Enter"}
          className="flex-1 min-w-[120px] text-sm outline-none py-0.5" />
        )}
      </div>
    </div>
  );
}

export default function Documents() {
  const KINDS = kindsFactory();
  const { tenantId, role, tenant, canConfigure, tenants, meUser } = useDash();
  const { labels: audienceLabels } = useAudiences(tenantId);
  const community = shortName(tenant);
  const [docs, setDocs] = useCachedState(`kb-docs:${tenantId}`, []);
  const liveDocs = useMemo(() => (docs || []).filter((d) => !d.archived), [docs]);
  const [moveTarget, setMoveTarget] = useState(null); // {item, kind}
  const [factSuggestion, setFactSuggestion] = useState(null);
  const [entries, setEntries] = useCachedState(`kb-entries:${tenantId}`, []);
  const [suggestions, setSuggestions] = useCachedState(`kb-sugs:${tenantId}`, []);
  const [dismissed, setDismissed] = useCachedState(`kb-dis:${tenantId}`, []);
  const [suggestingTags, setSuggestingTags] = useState(false);
  const [suggestingDocTags, setSuggestingDocTags] = useState(false);
  const [limits, setLimits] = useCachedState(`kb-limits:${tenantId}`, { facts: 0, facts_cap: 150, facts_full: false, facts_warn: false });
  const [teachOpen, setTeachOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [dragging, setDragging] = useState(false);
  const [queue, setQueue] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("kb_view") || "grid");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("kb_sort") || "recent");
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState("");
  const uploading = queue.some((q) => q.status === "uploading");
  useEffect(() => { localStorage.setItem("kb_view", view); }, [view]);
  useEffect(() => { localStorage.setItem("kb_sort", sortBy); }, [sortBy]);
  const [entryOpen, setEntryOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [checkingConflict, setCheckingConflict] = useState(false);
  const editBaselineRef = useRef({ title: "", description: "" });  // iter465b — content at open, so an audience/tags-only edit skips the conflict check
  const [rephrasing, setRephrasing] = useState(false);
  const [preRephrase, setPreRephrase] = useState(null); // user's original draft, for one-click undo
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sumDraft, setSumDraft] = useState("");
  const [sumBusy, setSumBusy] = useState(false);
  const fileRef = useRef(null);
  // Who may upload / edit AI documents — the SERVER decides (`can_edit_documents` on the community
  // record): the community's Redline-set dial (board seats only at the "+ board" rung), platform staff
  // always, and for an administrator-defined custom role its Knowledge & Documents permission (edit vs view).
  const isRedline = ["super_admin", "platform_admin", "onboarding_admin", "operator"].includes(role);
  const customView = meUser?.role === "custom" && meUser?.custom_perms?.documents !== "edit";
  const canEditDocs = !customView && (typeof tenant?.can_edit_documents === "boolean" ? tenant.can_edit_documents : isRedline);
  const readOnly = !canEditDocs;
  // iter211 — teaching the AI (the "Improve your AI" queue) is unlocked for board members
  // who've been granted the can_configure toggle, matching their settings-access grant.
  // Document uploads + legal settings stay on the stricter page-level `readOnly`.
  const teachReadOnly = role === "board_member" && !canConfigure && !canEditDocs;
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    if (!tenantId) return;
    const [d, k, s, ds, lim] = await Promise.all([
      api.get(`/tenants/${tenantId}/documents`).then((r) => r.data).catch(() => []),
      api.get(`/tenants/${tenantId}/knowledge`).then((r) => r.data).catch(() => []),
      api.get(`/tenants/${tenantId}/suggestions`).then((r) => r.data).catch(() => []),
      api.get(`/tenants/${tenantId}/suggestions?status=dismissed`).then((r) => r.data).catch(() => []),
      api.get(`/tenants/${tenantId}/knowledge/limits`).then((r) => r.data).catch(() => null),
    ]);
    setDocs(d); setEntries((k || []).filter((e) => e.kind === "fact" || e.kind === "announcement")); setSuggestions(s); setDismissed(ds);
    if (lim) setLimits(lim);
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  // Keep the sidebar "Teach your assistant" badge in sync without a page refresh — broadcast
  // the open-suggestion count whenever it changes (dismiss / resolve / restore / delete).
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("redline:sug-count", { detail: { tenantId, count: suggestions.length } }));
  }, [suggestions, tenantId]);

  // Live queue tracking: while anything is waiting/processing (or a rule scan is running),
  // refresh every 5s and toast when a document finishes — no manual reloads needed.
  const prevStatusRef = useRef({});
  const anyBusy = docs.some((d) => ["queued", "processing"].includes(d.status) || ["queued", "scanning"].includes(d.rules_scan_status));
  useEffect(() => {
    prevStatusRef.current = Object.fromEntries(docs.map((d) => [d.id, d.status]));
  }, [docs]);
  useEffect(() => {
    if (!anyBusy || !tenantId) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/tenants/${tenantId}/documents`);
        const fresh = r.data || [];
        const prev = prevStatusRef.current;
        fresh.forEach((d) => {
          if (["queued", "processing"].includes(prev[d.id]) && d.status === "indexed") {
            toast.success(`"${d.title}" is ready`, { description: "Your concierge can answer from it now." });
          }
        });
        setDocs(fresh);
      } catch { /* keep last state */ }
    }, 5000);
    return () => clearInterval(t);
  }, [anyBusy, tenantId]);

  // Deep-link from the Overview "flagged questions" banner: open Improve-your-AI directly.
  useEffect(() => {
    if (searchParams.get("improve") === "1") {
      setTeachOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("improve");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ⌘K deep-link: ?doc=<id> opens that document's preview once the library has loaded.
  useEffect(() => {
    const id = searchParams.get("doc");
    if (!id || !docs.length) return;
    const d = docs.find((x) => x.id === id);
    if (!d) toast.error("That document is no longer here.");
    else if (d.archived || d.historical) showArchived(d, "document");
    else preview(d);
    const next = new URLSearchParams(searchParams);
    next.delete("doc");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, docs]);

  // Expiry reminders and Mailbox's "Open the announcement" deep-link a record: ?entry=<id>.
  useEffect(() => {
    const id = searchParams.get("entry");
    if (!id || !entries.length) return;
    const e = entries.find((x) => x.id === id);
    if (!e) toast.error("That announcement or fact is no longer here.");
    else if (e.archived || e.expired) showArchived(e, e.kind || "announcement");
    else editEntry(e);
    const next = new URLSearchParams(searchParams);
    next.delete("entry");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, entries]);

  // A deep-linked record that has since been archived or expired. Opening the editor would be a
  // trap: the live list hides it and saving doesn't bring it back, so the manager edits something
  // the concierge still can't use (owner report — they had to find the Archive and hit Restore
  // themselves). Land them on the Archive with the record named and Restore one click away.
  const showArchived = (rec, kind) => {
    setTab("archive");
    setTimeout(() => document.getElementById("quick-knowledge")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    toast.info(`“${rec.title}” is archived`, {
      description: `It's here under Archive. Restore it to put the ${kind} back in front of the concierge.`,
      duration: 8000,
    });
  };

  const items = useMemo(() => {
    const kItems = entries.filter((e) => !e.archived && !e.expired).map((e) => ({ _type: e.kind, ...e }));
    let all = kItems;
    if (tab === "taught") all = kItems.filter((e) => e.source === "teach_assistant");
    else if (tab !== "all") all = kItems.filter((e) => e.kind === tab);
    if (activeTag) all = all.filter((it) => (it.tags || []).some((t) => t.toLowerCase() === activeTag.toLowerCase()));
    const q = query.trim().toLowerCase();
    if (q) all = all.filter((it) => `${it.title || ""} ${it.summary || ""} ${it.description || ""} ${(it.tags || []).join(" ")}`.toLowerCase().includes(q));
    const nm = (it) => (it.title || "").toLowerCase();
    const ty = (it) => it._type.toLowerCase();
    all = [...all].sort((a, b) => {
      if (sortBy === "name") return nm(a).localeCompare(nm(b));
      if (sortBy === "type") return ty(a).localeCompare(ty(b)) || nm(a).localeCompare(nm(b));
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return all;
  }, [entries, tab, query, sortBy, activeTag]);

  // Every tag in use, with how many entries carry it. iter327: this used to render as a wall of
  // ~40 chips six rows deep above the list, on web AND mobile — it's a dropdown now.
  const allTags = useMemo(() => {
    const seen = new Map();
    entries.forEach((e) => (e.tags || []).forEach((t) => {
      const k = t.toLowerCase();
      const row = seen.get(k) || { tag: t, n: 0 };
      row.n += 1;
      seen.set(k, row);
    }));
    return [...seen.values()].sort((a, b) => b.n - a.n || a.tag.localeCompare(b.tag));
  }, [entries]);

  const counts = useMemo(() => {
    const live = entries.filter((e) => !e.archived && !e.expired);
    return {
      all: live.length,
      taught: live.filter((e) => e.source === "teach_assistant").length,
      event: live.filter((e) => e.kind === "event").length,
      announcement: live.filter((e) => e.kind === "announcement").length,
      fact: live.filter((e) => e.kind === "fact").length,
      contact: live.filter((e) => e.kind === "contact").length,
      archive: entries.filter((e) => e.archived || e.expired || e.historical).length + (docs || []).filter((d) => d.archived || d.historical).length,
      expiring: [...entries, ...(docs || [])].filter((x) => !x.archived && x.expires_at && (() => { const [y, m, d] = x.expires_at.split("-").map(Number); return (new Date(y, m - 1, d) - new Date().setHours(0, 0, 0, 0)) / 86400000 <= 30; })()).length,
    };
  }, [entries, docs]);

  const SUPPORTED = ["pdf", "txt", "md", "csv", "docx", "xls", "xlsx", "pptx", "png", "jpg", "jpeg", "webp", "gif", "bmp", "tif", "tiff"];
  // Per-community cap (super-admin dial). Files over ~15 MB go up in small pieces so a
  // single big request can never trip proxy limits — with live % progress.
  const limitMb = Math.max(25, parseInt(tenant?.upload_limit_mb) || 25);
  const CHUNK_THRESHOLD = 15 * 1024 * 1024;

  const uploadChunked = async (file, fields, onPct) => {
    const start = await api.post(`/tenants/${tenantId}/uploads/start`, { filename: file.name, size: file.size });
    const { upload_id: uid, chunk_bytes: cb } = start.data;
    let sent = 0, index = 0;
    while (sent < file.size) {
      const fd = new FormData();
      fd.append("index", String(index));
      fd.append("file", file.slice(sent, sent + cb), "chunk.bin");
      await api.post(`/tenants/${tenantId}/uploads/${uid}/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      sent = Math.min(sent + cb, file.size);
      index += 1;
      onPct(Math.round((sent / file.size) * 100));
    }
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
    return api.post(`/tenants/${tenantId}/uploads/${uid}/finish`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };

  // New uploads always ASK who the AI may share them with (per-item audience) before saving.
  const [pendingUpload, setPendingUpload] = useState(null); // {files, folderId}
  const [pendingAudience, setPendingAudience] = useState(null); // chip keys, e.g. ["anyone"] or ["owners","board"]
  const [pendingRequireVerify, setPendingRequireVerify] = useState(false);
  const [pendingShareLink, setPendingShareLink] = useState(true);
  const [replaceMap, setReplaceMap] = useState({}); // file name → old doc id (superseded on upload)
  const [pendingExpiry, setPendingExpiry] = useState({ expires_at: "", expiry_action: "archive", expire_time: "" }); // optional, whole batch
  // Files in this batch that look like a newer version of a document already in the library.
  const versionMatches = useMemo(() => {
    const out = {};
    for (const f of pendingUpload?.files || []) {
      const old = liveDocs.find((d) => looksLikeNewVersion(f.name, d.filename || d.title || ""));
      if (old) out[f.name] = old;
    }
    return out;
  }, [pendingUpload, liveDocs]);

  const uploadFiles = (files, folderId = "") => {
    if (readOnly || !files?.length) return;
    setPendingAudience(null);
    setPendingRequireVerify(false);
    setPendingShareLink(true);
    setReplaceMap({});
    setPendingExpiry({ expires_at: "", expiry_action: "archive", expire_time: "" });
    setPendingUpload({ files: Array.from(files), folderId });
  };

  const confirmUpload = async () => {
    const { files, folderId } = pendingUpload || {};
    const audience = pendingAudience && pendingAudience.length ? pendingAudience : ["anyone"];
    const requireVerify = pendingRequireVerify;
    const replaces = { ...replaceMap };
    const expiry = { ...pendingExpiry };
    const shareLink = pendingShareLink;
    setPendingUpload(null);
    await doUpload(files || [], folderId, audience, requireVerify, replaces, expiry, shareLink);
  };

  const doUpload = async (files, folderId = "", audience = ["anyone"], requireVerify = false, replaces = {}, expiry = {}, shareLink = true) => {
    const audiencesCsv = (Array.isArray(audience) ? audience : [audience || "anyone"]).join(",");
    if (readOnly || !files?.length) return;
    for (const file of Array.from(files)) {
      const ext = file.name.split(".").pop().toLowerCase();
      const qid = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      if (file.size > limitMb * 1024 * 1024) {
        setQueue((q) => [{ id: qid, name: file.name, status: "error", error: `Larger than this ${V.lc("org")}'s ${limitMb} MB limit` }, ...q]);
        continue;
      }
      const setPct = (pct) => setQueue((q) => q.map((it) => (it.id === qid ? { ...it, pct } : it)));
      if (ext === "zip") {
        // Bulk import: the ZIP's folder tree becomes document folders.
        setQueue((q) => [{ id: qid, name: file.name, status: "uploading" }, ...q]);
        try {
          let r;
          if (file.size > CHUNK_THRESHOLD) {
            r = await uploadChunked(file, { folder_id: folderId || "", audiences: audiencesCsv }, setPct);
          } else {
            const fd = new FormData();
            fd.append("folder_id", folderId || "");
            fd.append("audiences", audiencesCsv);
            fd.append("file", file);
            r = await api.post(`/tenants/${tenantId}/documents/zip`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          }
          const { queued, folders_created: made, skipped } = r.data;
          setQueue((q) => q.map((it) => (it.id === qid ? { ...it, status: "added" } : it)));
          toast.success(`ZIP imported — ${queued} file${queued === 1 ? "" : "s"} queued${made ? `, ${made} folder${made === 1 ? "" : "s"} created` : ""}`, {
            description: skipped?.length
              ? `${skipped.length} item${skipped.length === 1 ? "" : "s"} skipped: ${skipped.slice(0, 3).map((s) => s.name.split("/").pop()).join(", ")}${skipped.length > 3 ? "…" : ""}`
              : "You'll get a notification as each file finishes indexing.",
          });
          setTimeout(() => setQueue((qq) => qq.filter((x) => x.id !== qid)), 4000);
        } catch (e) {
          setQueue((q) => q.map((it) => (it.id === qid ? { ...it, status: "error", error: e.response?.data?.detail || "ZIP import failed" } : it)));
        }
        continue;
      }
      if (!SUPPORTED.includes(ext)) {
        setQueue((q) => [{ id: qid, name: file.name, status: "error", error: `.${ext} isn't supported` }, ...q]);
        continue;
      }
      setQueue((q) => [{ id: qid, name: file.name, status: "uploading" }, ...q]);
      try {
        const fields = { title: file.name.replace(/\.[^.]+$/, ""), doc_type: "Document", folder_id: folderId || "", audiences: audiencesCsv, require_verification: requireVerify ? "true" : "",
          expires_at: expiry.expires_at || "", expiry_action: expiry.expiry_action || "archive", expire_time: expiry.expire_time || "", share_by_link: shareLink ? "1" : "0" };
        let r;
        if (file.size > CHUNK_THRESHOLD) {
          r = await uploadChunked(file, fields, setPct);
        } else {
          const fd = new FormData();
          Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
          fd.append("file", file);
          r = await api.post(`/tenants/${tenantId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        setQueue((q) => q.map((it) => (it.id === qid ? { ...it, status: "added", docId: r.data.id } : it)));
        const newId = r.data.id;
        if (replaces[file.name] && newId) {
          try {
            const sr = await api.post(`/tenants/${tenantId}/documents/${newId}/supersede`, { old_id: replaces[file.name] });
            toast.success(`“${file.name}” replaces “${sr.data.replaces_title}”`, { description: "The older version moved to the Archive." });
          } catch (e) { toast.error(e.response?.data?.detail || "Uploaded, but couldn't archive the older version"); }
        }
        toastAdded(`“${file.name}”`, community, async () => {
          try { await api.delete(`/tenants/${tenantId}/documents/${newId}`); toast(`Removed from ${community}`); load(); } catch { toast.error("Couldn't undo"); }
        });
        // Auto-clear the confirmation from the drop zone; the file lives in the library below.
        setTimeout(() => setQueue((qq) => qq.filter((x) => x.id !== qid)), 3000);
      } catch (e) {
        setQueue((q) => q.map((it) => (it.id === qid ? { ...it, status: "error", error: e.response?.data?.detail || "Upload failed" } : it)));
      }
    }
    load();
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); };

  const openEntry = (kind) => {
    if (kind === "fact" && limits?.facts_full) {
      toast.error(`You're at the ${limits.facts_cap}-fact limit. Delete or merge older facts to add new ones.`);
      return;
    }
    setForm({ ...EMPTY_FORM, kind }); setPreRephrase(null); setFactSuggestion(null); setConflicts([]); editBaselineRef.current = { title: "", description: "" }; setEntryOpen(true);
  };
  const editEntry = (e) => { setForm({ id: e.id, kind: e.kind, title: e.title || "", description: e.description || "", date: e.date || "", time: e.time || "", expire_time: e.expire_time || "", tags: e.tags || [], audiences: chipsOf(e), require_verification: !!e.require_verification, expires_at: e.expires_at || "", expiry_action: e.expiry_action || "archive" }); setPreRephrase(null); setFactSuggestion(null); setConflicts([]); editBaselineRef.current = { title: e.title || "", description: e.description || "" }; setEntryOpen(true); };

  // iter464 — the actual write, once any conflict nudge has been acknowledged (or there was none).
  const doSave = async () => {
    if (readOnly) return;
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    setSaving(true);
    try {
      const payload = { kind: form.kind, title: form.title, description: form.description, date: form.date || null, time: form.time || null, expire_time: (form.kind === "announcement" || form.kind === "fact") ? (form.expire_time || null) : null, tags: form.tags || [], audiences: form.audiences && form.audiences.length ? form.audiences : ["anyone"], require_verification: !!form.require_verification,
        expires_at: form.kind === "fact" && form.expires_at ? form.expires_at : null, expiry_action: form.expiry_action || "archive" };      if (form.id) {
        await api.put(`/tenants/${tenantId}/knowledge/${form.id}`, payload);
        toast.success("Updated — your concierge now uses the corrected answer");
      } else {
        const r = await api.post(`/tenants/${tenantId}/knowledge`, payload);
        const newId = r.data?.id;
        toastAdded(KINDS[form.kind].label, community, async () => {
          try { await api.delete(`/tenants/${tenantId}/knowledge/${newId}`); toast(`Removed from ${community}`); load(); } catch { toast.error("Couldn't undo"); }
        });
      }
      setEntryOpen(false); setForm(EMPTY_FORM); setConflicts([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const saveEntry = async () => {
    if (readOnly) return;  // board members are view-only — dialog is locked; defense-in-depth
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    // iter464 — conflict nudge: for prose answers, first check whether this contradicts an
    // existing document/fact. iter465b — ONLY when the title/description actually changed, so an
    // audience-only (or tags/expiry-only) edit saves immediately and is never blocked by a nudge.
    const b = editBaselineRef.current;
    const contentChanged = (form.title || "").trim() !== (b.title || "").trim() || (form.description || "").trim() !== (b.description || "").trim();
    const check = contentChanged && !!form.description.trim() && form.description.trim().length >= 8 && !["event", "contact"].includes(form.kind);
    if (check) {
      setCheckingConflict(true);
      try {
        const r = await api.post(`/tenants/${tenantId}/knowledge/check-conflict`, { title: form.title, description: form.description, kind: form.kind, entry_id: form.id || "" });
        const found = r.data?.conflicts || [];
        if (found.length) { setConflicts(found); setCheckingConflict(false); return; }  // halt — show the nudge
      } catch { /* best-effort — never block a save on the checker */ }
      setCheckingConflict(false);
    }
    await doSave();
  };

  const suggestFactExpiry = async () => {
    if (form.kind !== "fact" || form.expires_at || readOnly) return;
    const text = `${form.title} ${form.description}`;
    if (!/\b(until|through|thru|closed|temporar|this week|next week|this month|this weekend|for the season|reopen|starting|ends?|expires?|deadline|due|by (mon|tue|wed|thu|fri|sat|sun|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)|\d{1,2}\/\d{1,2})\b/i.test(text)) return;
    try {
      const r = await api.post(`/tenants/${tenantId}/knowledge/suggest-expiry`, { title: form.title, description: form.description });
      setFactSuggestion(r.data?.suggestion ? { ...r.data.suggestion } : null);
    } catch { /* suggestion is best-effort */ }
  };

  const archiveDoc = async (d) => {
    try {
      await api.post(`/tenants/${tenantId}/documents/${d.id}/archive`, {});
      toast.success(`“${d.title}” archived`, { description: "Out of the concierge's answers, kept for your records. Restore anytime from the Archive tab." });
      setPreviewDoc(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't archive"); }
  };
  const archiveEntry = async (e) => {
    try {
      await api.post(`/tenants/${tenantId}/knowledge/${e.id}/archive`, {});
      toast.success(`“${e.title}” archived`, { description: "Restore anytime from the Archive tab." });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't archive"); }
  };
  const patchDocExpiry = async (d, patch) => {
    try {
      await api.patch(`/tenants/${tenantId}/documents/${d.id}`, patch);
      const merge = (x) => ({ ...x, ...("expires_at" in patch ? { expires_at: patch.expires_at, expire_time: patch.expire_time || null, expiry_source: patch.expires_at ? "manual" : null } : {}),
        ...(patch.expiry_action ? { expiry_action: patch.expiry_action } : {}),
        ...(("expires_at" in patch || patch.dismiss_expiry_suggestion) ? { expiry_suggestion: { ...(x.expiry_suggestion || {}), dismissed: true, applied: !!patch.expires_at } } : {}) });
      setDocs((ds) => ds.map((x) => (x.id === d.id ? merge(x) : x)));
      setPreviewDoc((x) => (x && x.id === d.id ? merge(x) : x));
      if ("expires_at" in patch) toast.success(patch.expires_at ? `Expires ${new Date(patch.expires_at + "T00:00").toLocaleDateString()}` : "No expiry — it stays until you archive it");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
  };

  const removeDoc = async (id) => {
    const r = await api.delete(`/tenants/${tenantId}/documents/${id}`);
    const paused = r.data?.rules_paused || 0;
    toast.success(paused
      ? `Document removed · ${paused} rule${paused === 1 ? "" : "s"} paused for review`
      : "Document removed");
    load();
  };
  const retryDoc = async (id) => {
    try { await api.post(`/tenants/${tenantId}/documents/${id}/reprocess`); toast.success("Back in the queue", { description: "You'll get a notification when it's ready." }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not retry"); }
  };
  const removeEntry = async (id) => { await api.delete(`/tenants/${tenantId}/knowledge/${id}`); toast.success("Removed"); load(); };

  const resolveSuggestion = async (sugId, answer, notify, documentId, audiences, requireVerification) => {
    const { data } = await api.post(`/tenants/${tenantId}/suggestions/${sugId}/resolve`, {
      answer, notify: !!notify, document_id: documentId || "",
      audiences: audiences && audiences.length ? audiences : ["anyone"], require_verification: !!requireVerification,
    });
    const n = data?.notified || 0;
    const docBit = data?.entry?.document_title ? ` — it'll send “${data.entry.document_title}”${data.alias_added ? ` and now knows “${data.alias_added}” means that file` : ""}` : "";
    toast.success(n > 0
      ? `Nice — your concierge learned that${docBit} and texted the answer to ${n} ${V.lc("person")}${n === 1 ? "" : "s"} who asked`
      : `Nice — your concierge just learned that${docBit}`);
    load();
  };
  const restoreSuggestion = async (sugId) => {
    await api.post(`/tenants/${tenantId}/suggestions/${sugId}/restore`);
    load();
  };
  const deleteSuggestion = async (sugId) => {
    await api.delete(`/tenants/${tenantId}/suggestions/${sugId}`);
    toast.success("Deleted");
    load();
  };
  const clearDismissed = async () => {
    const { data } = await api.post(`/tenants/${tenantId}/suggestions/clear-dismissed`);
    toast.success(data?.deleted ? `Cleared ${data.deleted} dismissed item${data.deleted === 1 ? "" : "s"}` : "Nothing to clear");
    load();
  };
  const dismissSuggestion = async (sugId) => {
    await api.post(`/tenants/${tenantId}/suggestions/${sugId}/dismiss`);
    toast("Moved to Dismissed", { description: "It won't show under Needs answer.", position: "bottom-center", action: { label: "Undo", onClick: () => restoreSuggestion(sugId) } });
    load();
  };
  const linkSuggestion = async (sugId, knowledgeId) => {
    await api.post(`/tenants/${tenantId}/suggestions/${sugId}/link`, { knowledge_id: knowledgeId });
    toast.success("Linked — your concierge will recognize this phrasing next time");
    load();
  };
  // iter195p — fetchRelated now (a) returns a shape { items, error } instead of a
  // bare array so the caller can surface real failures (Cloudflare 520 / backend
  // crash / timeout) as a "retry?" banner instead of the misleading "No matches",
  // and (b) auto-retries ONCE on any failure. Field bug: production intermittently
  // returned 520 (backend crash or edge timeout on the RAG-heavy path), the UI
  // caught it silently as `[]`, and the manager thought their fact library was
  // empty. One retry catches transient cold-start / rate-limit hiccups, the error
  // banner surfaces the rest so the manager knows to try again vs. add a new fact.
  const fetchRelated = async (sugId, q = "") => {
    const url = q.trim()
      ? `/tenants/${tenantId}/suggestions/${sugId}/related?q=${encodeURIComponent(q.trim())}&limit=6`
      : `/tenants/${tenantId}/suggestions/${sugId}/related`;
    const tryOnce = async () => {
      const { data } = await api.get(url);
      return { items: data?.related || [], error: data?.error || null, timedOut: !!data?.timed_out, fast: !!data?.fast };
    };
    try {
      const first = await tryOnce();
      // Backend returned successfully (200) but with an error payload — retry once.
      if (first.error && !first.items.length) {
        try {
          const second = await tryOnce();
          return second.items.length ? second : first;
        } catch { return first; }
      }
      return first;
    } catch (err) {
      // Network/520 — retry once after a short delay.
      await new Promise((r) => setTimeout(r, 600));
      try { return await tryOnce(); }
      catch (err2) {
        const status = err2?.response?.status || err?.response?.status;
        return { items: [], error: status === 520 ? "Server unreachable (Cloudflare 520). Please retry." : (err2?.message || err?.message || "Fetch failed"), timedOut: false, fast: false };
      }
    }
  };

  const preview = async (d) => {
    setPreviewDoc({ ...d, content: "" }); setPreviewLoading(true); setSumDraft(d.summary || "");
    try { const r = await api.get(`/tenants/${tenantId}/documents/${d.id}/content`); setPreviewDoc((p) => (p && p.id === d.id ? { ...p, ...r.data } : p)); setSumDraft(r.data.summary || ""); }
    catch { toast.error("Could not load preview"); }
    finally { setPreviewLoading(false); }
  };

  const saveSummary = async () => {
    setSumBusy(true);
    try {
      await api.patch(`/tenants/${tenantId}/documents/${previewDoc.id}`, { summary: sumDraft });
      setDocs((ds) => ds.map((x) => (x.id === previewDoc.id ? { ...x, summary: sumDraft } : x)));
      setPreviewDoc((p) => ({ ...p, summary: sumDraft }));
      toast.success("Summary saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSumBusy(false); }
  };

  const regenSummary = async () => {
    setSumBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/documents/${previewDoc.id}/resummarize`);
      setSumDraft(r.data.summary || "");
      setDocs((ds) => ds.map((x) => (x.id === previewDoc.id ? { ...x, summary: r.data.summary } : x)));
      toast.success("Summary regenerated");
    } catch { toast.error("Could not regenerate"); }
    finally { setSumBusy(false); }
  };

  // Aliases/tags edited right here in Preview (previously only reachable from the row menu).
  // Saves immediately; the AI matches these as authoritative alternate names for the file.
  const savePreviewTags = async (tags) => {
    if (!previewDoc) return;
    const id = previewDoc.id, prev = previewDoc.tags || [];
    setPreviewDoc((p) => (p && p.id === id ? { ...p, tags } : p));
    setDocs((ds) => ds.map((x) => (x.id === id ? { ...x, tags } : x)));
    try {
      await api.patch(`/tenants/${tenantId}/documents/${id}`, { tags });
    } catch (e) {
      setPreviewDoc((p) => (p && p.id === id ? { ...p, tags: prev } : p));
      setDocs((ds) => ds.map((x) => (x.id === id ? { ...x, tags: prev } : x)));
      toast.error(e.response?.data?.detail || "Could not update tags");
    }
  };

  const showDate = form.kind === "event" || form.kind === "announcement";
  const showTime = form.kind === "event";

  // AI rephrase (facts & announcements): polish the rough draft, keep every detail. The rewrite
  // is shown for approval first — it never overwrites what the manager wrote on its own.
  const [pendingRe, setPendingRe] = useState(null);
  const rephraseEntry = async (style) => {
    if (!form.title.trim() && !form.description.trim()) { toast.error("Write a rough draft first — then AI can polish it"); return; }
    setRephrasing(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/knowledge/rephrase`,
        { kind: form.kind, title: form.title, description: form.description, style });
      setPendingRe({ style, title: r.data.title, description: r.data.description });
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't rephrase just now"); }
    finally { setRephrasing(false); }
  };
  const acceptRephrase = () => {
    setPreRephrase((p) => p || { title: form.title, description: form.description });
    setForm((f) => ({ ...f, title: pendingRe.title, description: pendingRe.description }));
    setPendingRe(null);
    toast.success("Rephrased — undo if you change your mind");
  };
  const undoRephrase = () => {
    if (!preRephrase) return;
    setForm((f) => ({ ...f, ...preRephrase }));
    setPreRephrase(null);
    toast.success("Back to your original wording");
  };
  const { visible: visibleItems, remaining: itemsRemaining, showMore: showMoreItems } = usePaged(items, { step: 24, resetKey: tab });

  return (
    <div className="space-y-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <PageTitle title="AI Documents" about="Everything your concierge answers from. Drop in documents and add announcements and facts — it learns instantly. (Events and Trusted Pros now have their own tabs.)" />
          {tenant?.features?.websites && <p className="text-xs text-muted-foreground mt-1" data-testid="documents-website-knowledge-note">Pages, files and FAQs pulled from a website are managed under <Link to="/dashboard/websites" className="font-semibold text-ink underline decoration-sand-300 hover:decoration-ink">Website Knowledge</Link> — one place for their audience and link-sharing settings.</p>}
        </div>
        {(suggestions.length > 0 || dismissed.length > 0 || !readOnly) && (
          <div className="shrink-0 flex items-center gap-1">
            <button data-testid="teach-open" onClick={() => setTeachOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 text-amber-800 font-semibold text-sm px-4 py-2 hover:bg-amber-100 transition-colors">
              <Sparkles className="h-4 w-4" strokeWidth={1.75} /> Teach your AI
              {suggestions.length > 0 && <span className="text-[11px] font-bold bg-amber-200 text-amber-900 rounded-full px-1.5 py-0.5">{suggestions.length}</span>}
            </button>
            <InfoHint title="Improve your AI" steps={[
              "Your AI flags questions it couldn't answer confidently",
              "You add a one-line answer (or dismiss it)",
              "It learns instantly — no technical setup or retraining",
            ]}>
              When {V.a("person")} asks something your AI doesn't have an answer for, it doesn't guess — it saves the question here so you can add the answer once, and it'll know it for everyone next time.
            </InfoHint>
          </div>
        )}
      </div>

      {/* Sticky in-page nav — jump to any section without hunting through the page. Sticks
          under the top bar so it's always reachable once the user starts scrolling. */}
      <SectionNav counts={{ docs: liveDocs.length, quick: counts.all, archive: counts.archive, expiring: counts.expiring }} />

      <TruncationBanner docs={docs} />

      <TeachAssistant open={teachOpen} onOpenChange={setTeachOpen} suggestions={suggestions} dismissed={dismissed} readOnly={teachReadOnly}
        onResolve={resolveSuggestion} onDismiss={dismissSuggestion} onRestore={restoreSuggestion}
        onDelete={deleteSuggestion} onClearDismissed={clearDismissed}
        onLink={linkSuggestion} onFetchRelated={fetchRelated} library={{ docs: liveDocs, entries }}
        canUpload={!readOnly} uploadFiles={uploadFiles} queue={queue} onDismissQueue={(id) => setQueue((qq) => qq.filter((x) => x.id !== id))} />

      {/* Quick add — structured entries + voice, with a compact drop box underneath */}
      <div id="add-knowledge" className="scroll-mt-24 bg-white rounded-3xl border border-sand-200 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-heading font-extrabold text-ink flex-wrap"><Sparkles className="h-4 w-4 text-redline-500" strokeWidth={2} /> Add knowledge
              <CommunityChip tenant={tenant} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Add an announcement or fact, say it out loud, or drop in documents — everything here goes to <span className="font-semibold text-ink">{community}</span>'s concierge.</p>
          </div>
          <div className="shrink-0 grid grid-cols-2 gap-2 sm:flex sm:items-center [&>*]:min-w-0 [&_button]:w-full [&_button]:justify-center sm:[&_button]:w-auto">
            <ImportQA tenantId={tenantId} readOnly={readOnly} onSaved={load} />
            <VoiceNote tenantId={tenantId} readOnly={readOnly} onSaved={load} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(KINDS).map(([k, cfg]) => {
            const factLimitHit = k === "fact" && limits?.facts_full;
            return (
              <button key={k} data-testid={`add-${k}-btn`} disabled={readOnly || factLimitHit} onClick={() => openEntry(k)}
                title={factLimitHit ? `You've hit the ${limits.facts_cap}-fact limit — delete or merge older ones to add more.` : cfg.hint}
                className="group flex items-center gap-3 rounded-2xl border border-sand-200 p-3 text-left hover:border-redline-300 hover:bg-redline-50/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99]">
                <span className={`h-9 w-9 rounded-xl ${cfg.bg} flex items-center justify-center shrink-0`}>
                  <cfg.icon className={`h-4.5 w-4.5 ${cfg.color}`} strokeWidth={1.75} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold text-ink truncate">{cfg.label}{k === "fact" && limits?.facts_cap ? <span className={`ml-1.5 text-[11px] font-semibold ${limits?.facts_full ? "text-red-600" : limits?.facts_warn ? "text-amber-600" : "text-ink-400"}`}>{limits.facts}/{limits.facts_cap}</span> : null}</span>
                  <span className="block text-[11px] text-muted-foreground leading-tight truncate">{factLimitHit ? "Limit reached — delete or merge to add more" : cfg.hint}</span>
                </span>
                <Plus className="h-4 w-4 text-ink-300 group-hover:text-redline-500 transition-colors shrink-0" />
              </button>
            );
          })}
        </div>

        {/* compact drag & drop — pulled up right under the quick-add categories */}
        <div
          data-testid="upload-zone"
          onDragOver={(e) => { e.preventDefault(); if (!readOnly) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => !readOnly && fileRef.current?.click()}
          className={`mt-4 rounded-2xl border-2 border-dashed px-4 py-4 sm:px-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 cursor-pointer transition-all duration-200
            ${readOnly ? "opacity-60 cursor-not-allowed border-sand-200" :
              dragging ? "border-redline-500 bg-redline-50" : "border-sand-200 hover:border-redline-400 hover:bg-redline-50/40"}`}>
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${dragging ? "bg-redline-500" : "bg-redline-50"}`}>
              {uploading ? <Loader2 className={`h-5 w-5 animate-spin ${dragging ? "text-white" : "text-redline-500"}`} />
                : <UploadCloud className={`h-5 w-5 ${dragging ? "text-white" : "text-redline-500"}`} strokeWidth={1.75} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className={`font-heading font-bold ${dragging ? "text-base text-redline-700" : "text-sm"}`} data-testid="upload-zone-title">{dragging ? `Drop to add to ${tenant?.name || community}` : `Drag & drop documents for ${community}`}</div>
              <p className="text-xs text-muted-foreground line-clamp-2 sm:truncate">CCRs, bylaws, minutes, budgets — PDF, Word, Excel, PowerPoint, text, images (PNG/JPG — we read the text in them), or a <span className="font-semibold text-ink">.zip of whole folders</span> (its structure is recreated).</p>
            </div>
          </div>
          {!readOnly && (
            <span data-testid="browse-files-btn" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink-700 transition-colors w-full sm:w-auto">
              <UploadCloud className="h-4 w-4" /> Upload to {community}
            </span>
          )}
          <input ref={fileRef} type="file" multiple accept=".pdf,.txt,.md,.csv,.docx,.xls,.xlsx,.pptx,.zip,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tif,.tiff" hidden onChange={(e) => uploadFiles(e.target.files)} />
        </div>

        {queue.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="upload-queue">
            {queue.map((q) => (
              <div key={q.id} data-testid={`upload-item-${q.id}`} className="flex items-center gap-3 rounded-xl border border-sand-200 bg-white px-4 py-2.5">
                <div className="shrink-0">
                  {q.status === "uploading" && <Loader2 className="h-4 w-4 animate-spin text-ink/40" />}
                  {q.status === "added" && <Check className="h-4 w-4 text-emerald-600" />}
                  {q.status === "error" && <AlertCircle className="h-4 w-4 text-danger-600" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-ink truncate">{q.name}</div>
                  <div className={`text-xs ${q.status === "error" ? "text-danger-600" : q.status === "added" ? "text-emerald-600 font-medium" : "text-muted-foreground"}`}>
                    {q.status === "uploading" && (q.pct != null ? `Uploading… ${q.pct}%` : "Uploading…")}
                    {q.status === "added" && "File uploaded successfully"}
                    {q.status === "error" && (q.error || "Failed")}
                  </div>
                </div>
                {(q.status === "added" || q.status === "error") && (
                  <button data-testid={`upload-dismiss-${q.id}`} onClick={() => setQueue((qq) => qq.filter((x) => x.id !== q.id))} className="shrink-0 text-ink/30 hover:text-ink transition-colors"><X className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DOCUMENTS — pulled up as the main focus */}
      <div id="documents" className="scroll-mt-24 space-y-4">
        <ExpirySuggestionsStrip tenantId={tenantId} docs={docs} readOnly={readOnly} onChanged={load} />
        <DocumentsLibrary tenantId={tenantId} readOnly={readOnly} docs={liveDocs}
          onReloadDocs={load} onPreview={preview} uploadFiles={uploadFiles} removeDoc={removeDoc}
          onArchive={archiveDoc} onMove={(tenants || []).length > 1 ? (d) => setMoveTarget({ item: d, kind: "document" }) : null} />
      </div>

      <Link to="/dashboard/settings?tab=policies" data-testid="legal-compliance-pointer"
        className="flex items-center gap-2.5 rounded-2xl border border-dashed border-sand-300 bg-white/60 px-4 py-3 text-xs text-ink/60 hover:border-ink hover:text-ink transition-colors">
        <Scale className="h-4 w-4 shrink-0 text-redline-500" strokeWidth={1.75} />
        <span className="min-w-0 flex-1">Legal guardrails (AI disclaimer, statutory fallback) moved to <span className="font-semibold text-ink">Settings → Policies &amp; Standing</span>.</span>
        <ChevronDown className="h-4 w-4 -rotate-90 shrink-0" />
      </Link>

      <div id="quick-knowledge" className="scroll-mt-24">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-heading text-xl font-extrabold tracking-tight">Quick knowledge <span className="text-muted-foreground font-body font-normal text-base">· {counts.all} item{counts.all === 1 ? "" : "s"}</span></h2>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="kb-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…"
                className="rounded-full bg-white border border-sand-200 pl-9 pr-9 py-2 text-sm outline-none focus:border-ink w-44" />
              {query && (
                <button data-testid="kb-search-clear" onClick={() => setQuery("")} aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink transition-colors">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {allTags.length > 0 && (
              <div data-testid="kb-tag-filter" className="relative flex items-center">
                <Tag className={`h-3.5 w-3.5 absolute left-3 pointer-events-none z-10 ${activeTag ? "text-white/70" : "text-muted-foreground"}`} />
                <Select value={activeTag || "__all__"} onValueChange={(v) => setActiveTag(v === "__all__" ? "" : v)}>
                  <SelectTrigger data-testid="kb-tag-select"
                    className={`h-auto w-auto min-w-[9.5rem] gap-2 rounded-full border pl-8 pr-3 py-2 text-sm font-medium shadow-none focus:ring-0 focus:border-ink ${activeTag ? "bg-ink text-white border-ink [&>svg]:text-white" : "bg-white border-sand-200"}`}>
                    <SelectValue placeholder="All tags" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__all__">All tags</SelectItem>
                    {allTags.map(({ tag, n }) => (
                      <SelectItem key={tag} value={tag}>{tag} · {n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="relative flex items-center">
              <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground absolute left-3 pointer-events-none" />
              <select data-testid="kb-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="rounded-full bg-white border border-sand-200 pl-8 pr-3 py-2 text-sm font-medium outline-none focus:border-ink appearance-none cursor-pointer">
                <option value="recent">Most recent</option>
                <option value="name">Name (A–Z)</option>
                <option value="type">Type</option>
              </select>
            </div>
            <div className="flex rounded-full border border-sand-200 overflow-hidden bg-white">
              <button data-testid="view-grid" onClick={() => setView("grid")} className={`px-3 py-2 transition-colors ${view === "grid" ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}><LayoutGrid className="h-4 w-4" /></button>
              <button data-testid="view-list" onClick={() => setView("list")} className={`px-3 py-2 transition-colors ${view === "list" ? "bg-ink text-white" : "text-ink/50 hover:text-ink"}`}><List className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-sand-100 rounded-full p-1 flex-wrap h-auto mb-6">
            {[["all", "All"], ["taught", "Taught by AI"], ["announcement", "Announcements"], ["fact", "Facts"], ["archive", "Archive"]].map(([v, label]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-redline-600 text-sm px-4">
                {label}{" "}
                {v === "fact"
                  ? <span className={`ml-1.5 text-xs font-semibold ${limits?.facts_full ? "text-red-600" : limits?.facts_warn ? "text-amber-600" : "opacity-60"}`}>{limits?.facts ?? counts.fact ?? 0} / {limits?.facts_cap ?? 150}</span>
                  : <span className="ml-1.5 text-xs opacity-60">{counts[v] ?? 0}</span>}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {tab === "fact" && limits?.web_facts > 0 && (
          <p className="-mt-4 mb-6 text-xs text-muted-foreground" data-testid="knowledge-web-facts-note">
            Plus {limits.web_facts} quick fact{limits.web_facts === 1 ? "" : "s"} read from your website — those don't
            count toward the {limits.facts_cap}-fact limit and are managed in{" "}
            <Link to="/dashboard/websites" className="font-semibold text-redline-600 hover:underline">Website Knowledge</Link>.
          </p>
        )}

        <div className="min-h-[520px]">
        {tab === "archive" ? (
          <ArchivePanel tenantId={tenantId} docs={docs} entries={entries} readOnly={readOnly} onChanged={load} onPreview={preview} />
        ) : items.length === 0 ? (
          <div data-testid="knowledge-empty" className="bg-white rounded-3xl border border-sand-200 p-14 text-center flex flex-col items-center gap-3">
            <div className="h-14 w-14 rounded-2xl bg-redline-50 flex items-center justify-center"><FileStack className="h-7 w-7 text-redline-400" strokeWidth={1.5} /></div>
            <div className="font-heading font-extrabold text-lg">Your concierge is ready to learn</div>
            <p className="text-sm text-muted-foreground max-w-xs">Drop a document above, or add an announcement or fact — it'll start answering {V.lcs("person")} from it right away.</p>
          </div>
        ) : view === "list" ? (
          <div className="bg-white rounded-2xl border border-sand-200 divide-y divide-sand-100 overflow-hidden" data-testid="doc-list-view">
            {visibleItems.map((it) => (
              <ListRow key={it.id} it={it} readOnly={readOnly}
                onOpen={() => (it._type === "document" ? preview(it) : editEntry(it))}
                onRetry={() => retryDoc(it.id)}
                onDelete={() => (it._type === "document" ? removeDoc(it.id) : removeEntry(it.id))} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {visibleItems.map((it) => it._type === "document"
              ? <DocCard key={it.id} d={it} readOnly={readOnly} onPreview={() => preview(it)} onDelete={() => removeDoc(it.id)} onRetry={() => retryDoc(it.id)} />
              : <EntryCard key={it.id} e={it} readOnly={readOnly} onEdit={() => editEntry(it)} onDelete={() => removeEntry(it.id)}
                  onArchive={() => archiveEntry(it)} onMove={(tenants || []).length > 1 ? () => setMoveTarget({ item: it, kind: it.kind }) : null} />)}
          </div>
        )}
        <ShowMore remaining={itemsRemaining} onClick={showMoreItems} label="Show more" testid="quick-knowledge-show-more" />
        </div>
      </div>

      {/* Entry dialog */}
      <Dialog open={entryOpen} onOpenChange={(v) => { setEntryOpen(v); if (!v) setConflicts([]); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {(() => { const C = KINDS[form.kind].icon; return <span className={`h-8 w-8 rounded-xl ${KINDS[form.kind].bg} flex items-center justify-center`}><C className={`h-4 w-4 ${KINDS[form.kind].color}`} /></span>; })()}
              {readOnly ? "View" : form.id ? "Edit" : "Add"} {KINDS[form.kind].label.toLowerCase()}
            </DialogTitle>
            <DialogDescription>{readOnly ? "You have view-only access — ask a manager to make changes." : form.id ? "Update this and your concierge instantly uses the corrected version." : `Your concierge will start answering ${V.lcs("person")} from this right away.`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!readOnly && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(KINDS).map(([k, cfg]) => (
                <button key={k} data-testid={`entry-kind-${k}`} onClick={() => setForm((f) => ({ ...f, kind: k }))}
                  className={`text-xs rounded-full px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${form.kind === k ? "bg-ink text-white border-ink" : "bg-white border-sand-200 hover:border-ink"}`}>
                  <cfg.icon className="h-3.5 w-3.5" /> {cfg.label}
                </button>
              ))}
            </div>
            )}
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold">{form.kind === "contact" ? "Name / role" : "Title"}</label>
                {!readOnly && <DictationMic onText={(t) => setForm((f) => ({ ...f, title: (f.title ? f.title.trim() + " " : "") + t }))} title="Dictate title" />}
              </div>
              <input data-testid="entry-title-input" value={form.title} disabled={readOnly} onChange={(e) => { setForm({ ...form, title: e.target.value }); if (conflicts.length) setConflicts([]); }}
                className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50 disabled:text-ink/60 disabled:cursor-not-allowed"
                placeholder={form.kind === "event" ? `Quarterly ${V.Grp()} Meeting` : form.kind === "announcement" ? "North gate temporarily closed" : form.kind === "contact" ? "Landscaping vendor" : "Quiet hours"} />
            </div>
            {showDate && (
              <div className={`grid ${showTime ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
                <div>
                  <label className="text-sm font-semibold">{form.kind === "announcement" ? "Expires (optional)" : "Date"}</label>
                  <div className={`mt-1 ${form.kind === "announcement" ? "flex flex-wrap items-center gap-2" : ""}`}>
                    <input data-testid="entry-date-input" type="date" value={form.date} disabled={readOnly} onChange={(e) => setForm({ ...form, date: e.target.value })}
                      className={`${form.kind === "announcement" ? "flex-1 min-w-[10rem]" : "w-full"} rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50 disabled:text-ink/60 disabled:cursor-not-allowed`} />
                    {form.kind === "announcement" && (
                      <select data-testid="entry-expire-time" value={form.expire_time || ""} disabled={readOnly || !form.date} onChange={(e) => setForm({ ...form, expire_time: e.target.value })}
                        className="rounded-xl border border-sand-200 px-3 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50 disabled:text-ink/60 disabled:cursor-not-allowed">
                        {EXPIRE_TIMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    )}
                  </div>
                  {form.kind === "announcement" && (form.date
                    ? <p className="mt-1 text-[11px] text-muted-foreground" data-testid="entry-expire-preview">The concierge stops sharing it after <span className="font-semibold text-ink/80">{expiryPreview(form.date, form.expire_time, tenant?.timezone)}</span>, then it moves to the Archive.</p>
                    : <p className="mt-1 text-[11px] text-muted-foreground">No date — it stays until you archive it.</p>)}
                </div>
                {showTime && (
                  <div>
                    <label className="text-sm font-semibold">Time (optional)</label>
                    <input data-testid="entry-time-input" value={form.time} disabled={readOnly} onChange={(e) => setForm({ ...form, time: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50 disabled:text-ink/60 disabled:cursor-not-allowed" placeholder="6:00 PM" />
                  </div>
                )}
              </div>
            )}
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold">{form.kind === "contact" ? "Details (phone, email, hours)" : "Description"}</label>
                <div className="flex items-center gap-1.5">
                  {!readOnly && (form.kind === "fact" || form.kind === "announcement") && (
                    <>
                      {preRephrase && !rephrasing && (
                        <button data-testid="entry-rephrase-undo" onClick={undoRephrase} title="Restore your original wording"
                          className="inline-flex items-center gap-1 rounded-full border border-sand-200 px-2.5 py-1 text-[11px] font-semibold text-ink/60 hover:border-ink hover:text-ink transition-colors">
                          <RotateCcw className="h-3 w-3" /> Undo
                        </button>
                      )}
                      <RephraseStyleMenu busy={rephrasing} onPick={rephraseEntry} testId="entry-rephrase" />
                    </>
                  )}
                  {!readOnly && <TextAssist value={form.description} onChange={(next) => setForm((f) => ({ ...f, description: next }))}
                    surface="knowledge" testId="entry-desc-assist" micTitle="Dictate description" />}
                </div>
              </div>
              <textarea data-testid="entry-desc-input" rows={4} value={form.description} disabled={readOnly} onChange={(e) => { setForm({ ...form, description: e.target.value }); if (conflicts.length) setConflicts([]); }} onBlur={suggestFactExpiry}
                className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50 disabled:text-ink/60 disabled:cursor-not-allowed"
                placeholder={KINDS[form.kind].hint} />
              {pendingRe && (
                <div data-testid="entry-rephrase-preview" className="mt-2 rounded-xl border border-sand-200 bg-sand-50 p-3">
                  <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-ink/40">Suggested rewrite — nothing changes until you accept</div>
                  {pendingRe.title && <div className="text-sm font-bold text-ink">{pendingRe.title}</div>}
                  <div className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-ink/75">{pendingRe.description}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <button type="button" data-testid="entry-rephrase-accept" onClick={acceptRephrase}
                      className="rounded-full bg-redline-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-redline-600">Use this</button>
                    <button type="button" data-testid="entry-rephrase-reject" onClick={() => setPendingRe(null)}
                      className="rounded-full border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/55 hover:border-ink/30">Keep mine</button>
                  </div>
                </div>
              )}
            </div>
            <TagsField tags={form.tags || []} readOnly={readOnly} onChange={(tags) => setForm((f) => ({ ...f, tags }))}
              suggesting={suggestingTags}
              onSuggest={async () => {
                const title = (form.title || "").trim();
                const desc = (form.description || "").trim();
                if (!title && !desc) { toast.error("Add a title or description first — I need something to work from."); return; }
                setSuggestingTags(true);
                try {
                  const r = await api.post(`/tenants/${tenantId}/knowledge/suggest-tags`,
                                           { kind: form.kind || "fact", title, description: desc });
                  const existing = new Set((form.tags || []).map((t) => t.toLowerCase()));
                  const merged = [...(form.tags || [])];
                  let added = 0;
                  for (const t of r.data.tags || []) {
                    if (!existing.has(t.toLowerCase()) && merged.length < 8) { merged.push(t); added++; }
                  }
                  setForm((f) => ({ ...f, tags: merged }));
                  toast.success(added ? `Added ${added} tag${added === 1 ? "" : "s"}` : "No new tags to add — you already had good ones.");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't suggest tags — try again.");
                } finally { setSuggestingTags(false); }
              }} />
            <div>
              <label className="text-sm font-semibold">Who can the AI share this with?</label>
              <div className="mt-1.5">
                <AudiencePicker tenantId={tenantId} testid="entry-audience" readOnly={readOnly} audiences={form.audiences}
                  requireVerification={!!form.require_verification}
                  onAudiences={(list) => setForm((f) => ({ ...f, audiences: list }))}
                  onRefuseOpen={() => toast.warning("Identity-checked items can't be shared with \u201cAnyone\u201d \u2014 a texted code only proves phone possession, not membership.")} />
              </div>
              {form.require_verification && (
                <p className="mt-2 text-[11px] text-amber-700 flex items-start gap-1.5" data-testid="verify-audience-note">
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" strokeWidth={2} />
                  <span>Identity-check items can&rsquo;t be open to &ldquo;Anyone&rdquo;. A texted code proves someone controls a phone &mdash; not that they belong here.</span>
                </p>
              )}
            </div>
            <label data-testid="entry-require-verify" className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${form.require_verification ? "border-amber-400 bg-amber-50" : "border-sand-200 hover:border-ink"}`}>
              <input type="checkbox" checked={!!form.require_verification} disabled={readOnly} onChange={(e) => setForm((f) => ({ ...f, require_verification: e.target.checked, audiences: (e.target.checked && isOpen(f.audiences)) ? [ALL_PEOPLE] : f.audiences }))}
                className="mt-0.5 h-4 w-4 accent-amber-600" />
              <span>
                <span className="text-xs font-bold text-ink flex items-center gap-1"><Lock className="h-3 w-3" /> Require identity check before sharing</span>
                <span className="block text-[11px] text-muted-foreground mt-0.5">For door codes, gate PINs and other secrets — the AI texts a one-time code the {V.lc("person")} must send back before this is ever shared (voice: read back on the call).</span>
              </span>
            </label>
            {form.kind === "fact" && (
              <ExpiryField testid="entry-expiry" value={form.expires_at || ""} action={form.expiry_action || "archive"} disabled={readOnly}
                time={form.expire_time} tz={tenant?.timezone} showTime
                onChange={(p) => { setForm((f) => ({ ...f, expires_at: p.expires_at || "", expiry_action: p.expiry_action || "archive", expire_time: p.expire_time || "" })); if (p.expires_at) setFactSuggestion(null); }}
                suggestion={factSuggestion}
                onAcceptSuggestion={(d) => { setForm((f) => ({ ...f, expires_at: d, expiry_action: "archive" })); setFactSuggestion(null); }}
                onDismissSuggestion={() => setFactSuggestion(null)} />
            )}
            {readOnly ? (
              <button data-testid="entry-close-btn" onClick={() => setEntryOpen(false)}
                className="w-full rounded-full bg-sand-100 text-ink font-semibold py-3 hover:bg-sand-200 transition-colors">
                Close
              </button>
            ) : conflicts.length > 0 ? (
              <div data-testid="entry-conflict-nudge" className="rounded-2xl border border-amber-300 bg-amber-50 p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" strokeWidth={2} />
                  <div className="text-sm">
                    <p className="font-bold text-amber-900">Heads up — this may contradict what's already on file</p>
                    <p className="text-amber-800/80 text-xs mt-0.5">Your concierge treats a quick fact as the most up-to-date answer, so saving this will override the {conflicts.length === 1 ? "source" : "sources"} below. Reconcile them if that's not what you want.</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {conflicts.map((c, i) => (
                    <li key={i} data-testid={`entry-conflict-item-${i}`} className="rounded-xl border border-amber-200 bg-white/70 px-3 py-2 text-xs">
                      <div className="flex items-center gap-1.5 font-bold text-ink">
                        {c.source_type === "document" ? <FileText className="h-3.5 w-3.5 text-ink/50" /> : <Info className="h-3.5 w-3.5 text-sky-500" />}
                        <span className="truncate">{c.source_title}</span>
                        <span className="ml-auto shrink-0 text-[10px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5 bg-ink/5 text-ink/50">{c.source_type === "document" ? "Document" : "Quick fact"}</span>
                      </div>
                      {c.reason && <div className="text-amber-900/90 mt-1">{c.reason}</div>}
                      {c.quote && <div className="text-ink/50 mt-1 italic line-clamp-2">“{c.quote}”</div>}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2 pt-0.5">
                  <button data-testid="entry-conflict-edit-btn" onClick={() => setConflicts([])}
                    className="flex-1 rounded-full bg-white border border-amber-300 text-amber-900 font-semibold py-2.5 text-sm hover:bg-amber-100 transition-colors">
                    Let me edit
                  </button>
                  <button data-testid="entry-conflict-save-anyway-btn" onClick={doSave} disabled={saving}
                    className="flex-1 rounded-full bg-redline-500 text-white font-semibold py-2.5 text-sm hover:bg-redline-600 transition-colors disabled:opacity-60">
                    {saving ? "Saving…" : "Save anyway"}
                  </button>
                </div>
              </div>
            ) : (
            <button data-testid="entry-save-btn" onClick={saveEntry} disabled={saving || checkingConflict}
              className="w-full rounded-full bg-redline-500 text-white font-semibold py-3 hover:bg-redline-600 transition-colors disabled:opacity-60">
              {checkingConflict ? "Checking for conflicts…" : saving ? "Saving…" : form.id ? "Save changes" : `Save to ${community}`}
            </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* New uploads always ask who the AI may share them with before anything saves */}
      <Dialog open={!!pendingUpload} onOpenChange={(v) => { if (!v) setPendingUpload(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="upload-audience-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload to {community}?</DialogTitle>
            <DialogDescription>Check the {V.lc("org")}, then choose who the AI may share {pendingUpload?.files?.length === 1 ? "this file" : `these ${pendingUpload?.files?.length || 0} files`} with — you can change it per document anytime.</DialogDescription>
          </DialogHeader>
          <CommunityBanner tenant={tenant} countLabel={pendingUpload?.files?.length === 1 ? `${pendingUpload.files[0].name} · ${(pendingUpload.files[0].size / 1024 / 1024).toFixed(1)} MB` : `${pendingUpload?.files?.length || 0} files`} />
          {(pendingUpload?.files?.length || 0) > 1 && (
            <ul data-testid="upload-file-list" className="max-h-28 overflow-y-auto text-xs text-ink/70 space-y-0.5 -mt-1 px-1">
              {pendingUpload.files.map((f) => <li key={f.name} className="truncate">• {f.name} <span className="text-ink/40">· {(f.size / 1024 / 1024).toFixed(1)} MB</span></li>)}
            </ul>
          )}
          {Object.entries(versionMatches).map(([name, old]) => (
            <div key={name} data-testid={`upload-version-match-${old.id}`} className="rounded-xl border border-sky-200 bg-sky-50 px-3.5 py-2.5 text-xs">
              <div className="font-bold text-sky-900 flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" /> Looks like a new version of “{old.title}”</div>
              <div className="text-sky-800/80 mt-0.5 truncate">{name}</div>
              <div className="mt-2 flex gap-1.5">
                <button type="button" data-testid={`upload-version-replace-${old.id}`} onClick={() => setReplaceMap((m) => ({ ...m, [name]: old.id }))}
                  className={`rounded-full px-3 py-1 font-bold ${replaceMap[name] === old.id ? "bg-sky-600 text-white" : "border border-sky-300 text-sky-800 hover:bg-sky-100"}`}>Replace — archive the old one</button>
                <button type="button" data-testid={`upload-version-keep-${old.id}`} onClick={() => setReplaceMap((m) => { const n = { ...m }; delete n[name]; return n; })}
                  className={`rounded-full px-3 py-1 font-bold ${replaceMap[name] !== old.id ? "bg-sky-600 text-white" : "border border-sky-300 text-sky-800 hover:bg-sky-100"}`}>Keep both</button>
              </div>
            </div>
          ))}
          <div className="rounded-xl border border-sand-200 bg-sand-50/60 px-3.5 py-3" data-testid="upload-expiry-block">
            <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">Expires <span className="normal-case tracking-normal font-normal">(optional{(pendingUpload?.files?.length || 0) > 1 ? " · applies to every file in this batch" : ""})</span></div>
            <ExpiryField compact testid="upload-expiry" value={pendingExpiry.expires_at} action={pendingExpiry.expiry_action}
              time={pendingExpiry.expire_time} tz={tenant?.timezone} showTime
              onChange={(p) => setPendingExpiry({ expires_at: p.expires_at || "", expiry_action: p.expiry_action || "archive", expire_time: p.expire_time || "" })} />
            {!pendingExpiry.expires_at && <p className="mt-1.5 text-[11px] text-muted-foreground flex items-start gap-1"><Sparkles className="h-3 w-3 mt-0.5 text-amber-600 shrink-0" /> Leave blank and the AI reads each file for an expiration date — insurance certificates, permits and seasonal schedules get one automatically; you can change it on the document afterwards.</p>}
          </div>
          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground -mb-1">Who can the AI share {pendingUpload?.files?.length === 1 ? "it" : "them"} with?</div>
          <div className="rounded-xl border border-sand-200 px-3.5 py-3">
            <AudiencePicker tenantId={tenantId} testid="upload-audience" audiences={pendingAudience || []}
              requireVerification={pendingRequireVerify}
              onAudiences={(list) => setPendingAudience(list)}
              onRefuseOpen={() => { toast.warning("Identity-checked files can't be shared with \u201cAnyone\u201d."); setPendingAudience([ALL_PEOPLE]); }} />
            {!pendingAudience && <p className="mt-2 text-[11px] text-amber-700" data-testid="upload-audience-hint">Pick who the AI can share {pendingUpload?.files?.length === 1 ? "this file" : "these files"} with — it never shares a file with anyone outside its audience.</p>}
          </div>
          <label data-testid="upload-require-verify" className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${pendingRequireVerify ? "border-amber-400 bg-amber-50" : "border-sand-200 hover:border-ink"}`}>
            <input type="checkbox" checked={pendingRequireVerify} onChange={(e) => { const c = e.target.checked; setPendingRequireVerify(c); if (c && isOpen(pendingAudience)) setPendingAudience([ALL_PEOPLE]); }}
              className="mt-0.5 h-4 w-4 accent-amber-600" />
            <span>
              <span className="text-sm font-bold text-ink flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Require identity check before sharing</span>
              <span className="block text-xs text-muted-foreground mt-0.5">For door codes, gate PINs and other secrets — the AI verifies the {V.lc("person")} with a one-time code first.</span>
            </span>
          </label>
          {pendingRequireVerify && (
            <p className="text-[11px] text-amber-700 flex items-start gap-1.5 -mt-1" data-testid="upload-verify-note">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" strokeWidth={2} />
              <span>Can&rsquo;t be open to &ldquo;Anyone&rdquo;. An identity code proves phone possession, not membership &mdash; so pick the groups it&rsquo;s meant for.</span>
            </p>
          )}
          <label data-testid="upload-share-link" className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${pendingShareLink ? "border-sand-200 hover:border-ink" : "border-amber-400 bg-amber-50"}`}>
            <input type="checkbox" data-testid="upload-share-link-checkbox" checked={pendingShareLink} onChange={(e) => setPendingShareLink(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-sand-300 accent-redline-500" />
            <span className="text-sm">
              <span className="font-semibold text-ink flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" strokeWidth={2} /> {V.many("person")} can be texted a download link</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                {pendingShareLink
                  ? <>The concierge answers from this file and, <span className="font-semibold text-ink">only when someone asks for a copy</span>, texts a secure link. It never offers the file on its own.</>
                  : <>The concierge still answers questions from this file but <span className="font-semibold text-ink">never sends it</span>. Use this for anything you wouldn't want forwarded — policies with account numbers, contracts, legal letters, vendor bids.</>}
              </span>
            </span>
          </label>
          <button data-testid="upload-audience-confirm" onClick={confirmUpload} disabled={!pendingAudience || !pendingAudience.length}
            className="w-full rounded-full bg-redline-500 text-white font-semibold py-3 hover:bg-redline-600 transition-colors disabled:opacity-50">
            {pendingAudience && pendingAudience.length ? `Upload ${pendingUpload?.files?.length === 1 ? "" : `${pendingUpload?.files?.length || 0} files `}to ${community}` : "Pick an audience to continue"}
          </button>
        </DialogContent>
      </Dialog>

      {/* Document preview */}
      <Dialog open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><FileText className="h-5 w-5 text-redline-600" strokeWidth={1.5} />{previewDoc?.title}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">Indexed content of this document.</DialogDescription>
          <div className="flex items-center justify-between gap-2 -mt-2 flex-wrap">
            <div className="text-xs text-muted-foreground">{previewDoc?.doc_type}{previewDoc?.filename ? ` · ${previewDoc.filename}` : ""}{previewDoc?.word_count ? ` · ${previewDoc.word_count.toLocaleString()} words` : ""}</div>
            {previewDoc?.storage_path && (
              <div className="flex items-center gap-2">
                <a data-testid="doc-open-original" href={fileUrl(previewDoc.storage_path)} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-3 py-1.5 text-xs font-semibold text-ink/70 hover:border-ink hover:text-ink transition-colors"><Eye className="h-3.5 w-3.5" /> Open in new tab</a>
                <a data-testid="doc-download-original" href={fileUrl(previewDoc.storage_path)} download={previewDoc.filename || true}
                  className="inline-flex items-center gap-1.5 rounded-full bg-ink text-white px-3 py-1.5 text-xs font-semibold hover:bg-ink-700 transition-colors"><Download className="h-3.5 w-3.5" /> Download</a>
              </div>
            )}
          </div>

          {previewDoc?.storage_path && (() => {
            const fn = (previewDoc.filename || "").toLowerCase();
            const url = fileUrl(previewDoc.storage_path);
            if (fn.endsWith(".pdf")) return (
              <div className="mt-2 rounded-xl overflow-hidden border border-sand-200 bg-sand-50">
                <iframe data-testid="doc-inline-pdf" title="Document preview" src={url} className="w-full h-[58vh]" />
              </div>
            );
            if (/\.(png|jpe?g|gif|webp|bmp)$/.test(fn)) return (
              <div className="mt-2 rounded-xl overflow-hidden border border-sand-200 bg-sand-50 flex justify-center p-2">
                <img data-testid="doc-inline-img" src={url} alt={previewDoc.title} className="max-h-[58vh] object-contain" />
              </div>
            );
            // Office/other formats can't render inline — the extracted text below is the preview.
            return (
              <div data-testid="doc-inline-unsupported" className="mt-2 rounded-xl border border-sand-200 bg-sand-50 px-4 py-3 text-xs text-muted-foreground flex items-center gap-2">
                <Eye className="h-4 w-4 shrink-0" /> This file type can't be shown inline — use <span className="font-semibold text-ink">Open in new tab</span> or <span className="font-semibold text-ink">Download</span> to view the original. The extracted text your AI reads is below.
              </div>
            );
          })()}

          {previewDoc?.status !== "processing" && (
            <div className="mt-2 rounded-xl border border-sand-200 bg-sand-50/60 p-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-redline-500" /> Summary</div>
                {!readOnly && (
                  <button data-testid="doc-regen-summary" onClick={regenSummary} disabled={sumBusy}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink/50 hover:text-redline-600 transition-colors disabled:opacity-50">
                    {sumBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Regenerate
                  </button>
                )}
              </div>
              <textarea data-testid="doc-summary-edit" rows={4} value={sumDraft} disabled={readOnly || sumBusy}
                onChange={(e) => setSumDraft(e.target.value)}
                placeholder="Add a short summary so managers can scan this at a glance…"
                className="w-full rounded-lg border border-sand-200 bg-white px-3 py-2.5 text-[15px] leading-relaxed outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50 resize-y min-h-[96px]" />
              {!readOnly && sumDraft !== (previewDoc?.summary || "") && (
                <button data-testid="doc-summary-save" onClick={saveSummary} disabled={sumBusy}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-xs font-semibold px-3.5 py-1.5 hover:bg-ink-700 transition-colors disabled:opacity-60"><Check className="h-3.5 w-3.5" /> Save summary</button>
              )}
            </div>
          )}

          {previewDoc?.status !== "processing" && (
            <div className="mt-2 rounded-xl border border-sand-200 bg-sand-50/60 p-3" data-testid="doc-preview-tags">
              <TagsField tags={previewDoc?.tags || []} onChange={savePreviewTags} readOnly={readOnly} />
              {!readOnly && <p className="mt-1.5 text-[11px] text-muted-foreground">Aliases are authoritative — tag a “Bylaws” file <span className="font-semibold text-ink/70">CC&amp;Rs</span> and the AI will find it by that name too.</p>}
            </div>
          )}

          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground mt-1">AI shares with</div>
          <div data-testid="doc-audience-picker">
            <AudiencePicker tenantId={tenantId} testid="doc-preview-audience" readOnly={readOnly || !previewDoc} compact
              audiences={previewDoc ? chipsOf(previewDoc) : ["anyone"]} requireVerification={!!previewDoc?.require_verification}
              onRefuseOpen={() => toast.warning("Identity-checked files can't be shared with \u201cAnyone\u201d.")}
              onAudiences={async (list) => {
                try {
                  const r = await api.patch(`/tenants/${tenantId}/documents/${previewDoc.id}`, { audiences: list });
                  const upd = { audiences: r.data?.audiences || list, audience: r.data?.audience || previewDoc.audience };
                  setDocs((ds) => ds.map((x) => (x.id === previewDoc.id ? { ...x, ...upd } : x)));
                  setPreviewDoc((d) => (d ? { ...d, ...upd } : d));
                  toast.success(`AI shares this with: ${describeChips(upd.audiences, audienceLabels)}`);
                } catch { toast.error("Could not update audience"); }
              }} />
          </div>

          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground mt-1">Expiry</div>
          {previewDoc && (previewDoc.archived || previewDoc.historical) ? (
            <div data-testid="doc-preview-archived-note" className="rounded-xl border border-sand-200 bg-sand-50 px-3 py-2 text-xs text-ink/70 flex items-center gap-2 flex-wrap">
              <ExpiryBadge item={previewDoc} />
              <span>{previewDoc.archived ? "Out of the concierge's answers — restore it from the Archive tab." : "Cited only as past information."}</span>
            </div>
          ) : previewDoc && (
            <ExpiryField compact testid="doc-expiry" value={previewDoc.expires_at || ""} action={previewDoc.expiry_action || "archive"} disabled={readOnly}
              time={previewDoc.expire_time} tz={tenant?.timezone} showTime
              onChange={(p) => patchDocExpiry(previewDoc, p)} suggestion={previewDoc.expiry_suggestion}
              onAcceptSuggestion={(d) => patchDocExpiry(previewDoc, { expires_at: d, expiry_action: "archive" })}
              onDismissSuggestion={() => patchDocExpiry(previewDoc, { dismiss_expiry_suggestion: true })} />
          )}
          {previewDoc?.expires_at && !previewDoc.archived && ["insurance", "permit", "contract"].includes(previewDoc.expiry_kind || previewDoc.expiry_suggestion?.kind) && !readOnly && (
            <Link to={`/dashboard/renewals?new=1&title=${encodeURIComponent(previewDoc.title || "")}&due=${previewDoc.expires_at}&category=${previewDoc.expiry_kind || previewDoc.expiry_suggestion?.kind}`}
              data-testid="doc-track-renewal" className="inline-flex items-center gap-1.5 text-xs font-bold text-redline-600 hover:text-redline-700 -mt-1">
              <CalendarDays className="h-3.5 w-3.5" /> Track this in Renewals →
            </Link>
          )}
          {!readOnly && previewDoc && !previewDoc.archived && (
            <div className="flex flex-wrap gap-2 -mt-1" data-testid="doc-preview-actions">
              <button type="button" data-testid="doc-preview-archive" onClick={() => archiveDoc(previewDoc)}
                className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-3 py-1.5 text-xs font-semibold text-ink/70 hover:border-ink hover:text-ink"><Archive className="h-3.5 w-3.5" /> Archive</button>
              {(tenants || []).length > 1 && (
                <button type="button" data-testid="doc-preview-move" onClick={() => setMoveTarget({ item: previewDoc, kind: "document" })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 px-3 py-1.5 text-xs font-semibold text-ink/70 hover:border-ink hover:text-ink"><ArrowRightLeft className="h-3.5 w-3.5" /> Move to another {V.lc("org")}</button>
              )}
            </div>
          )}

          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground mt-1 flex items-center justify-between gap-2">
            <span>Aliases <span className="text-[10px] normal-case font-medium text-ink-400">— words {V.lcs("person")} actually say for this doc (feeds AI retrieval)</span></span>
            <button type="button" data-testid="doc-preview-tags-suggest"
              disabled={readOnly || !previewDoc || suggestingDocTags || (previewDoc?.tags || []).length >= 8}
              onClick={async () => {
                setSuggestingDocTags(true);
                try {
                  const r = await api.post(`/tenants/${tenantId}/documents/${previewDoc.id}/suggest-tags`);
                  const existing = new Set((previewDoc.tags || []).map((t) => t.toLowerCase()));
                  const merged = [...(previewDoc.tags || [])];
                  let added = 0;
                  for (const t of r.data.tags || []) {
                    if (!existing.has(t.toLowerCase()) && merged.length < 10) { merged.push(t); added++; }
                  }
                  await api.patch(`/tenants/${tenantId}/documents/${previewDoc.id}`, { tags: merged });
                  setDocs((ds) => ds.map((x) => (x.id === previewDoc.id ? { ...x, tags: merged } : x)));
                  setPreviewDoc((d) => (d ? { ...d, tags: merged } : d));
                  toast.success(added ? `Added ${added} alias${added === 1 ? "" : "es"}` : "No new aliases to add.");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't suggest aliases");
                } finally { setSuggestingDocTags(false); }
              }}
              className="flex items-center gap-1 rounded-full bg-redline-50 text-redline-600 border border-redline-200 px-2 py-0.5 text-[10px] font-bold hover:bg-redline-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors normal-case tracking-normal">
              {suggestingDocTags ? <div className="h-2.5 w-2.5 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /> : <Sparkles className="h-2.5 w-2.5" strokeWidth={2} />}
              Suggest
            </button>
          </div>
          <div data-testid="doc-preview-tags" className="flex flex-wrap items-center gap-1.5">
            {(previewDoc?.tags || []).map((t) => (
              <span key={t} className="group inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 border border-sky-100 px-2.5 py-0.5 text-xs font-semibold">
                <Tag className="h-3 w-3" /> {t}
                {!readOnly && (
                  <button type="button" data-testid={`doc-preview-tag-remove-${t}`}
                    onClick={async () => {
                      const next = (previewDoc.tags || []).filter((x) => x !== t);
                      await api.patch(`/tenants/${tenantId}/documents/${previewDoc.id}`, { tags: next });
                      setDocs((ds) => ds.map((x) => (x.id === previewDoc.id ? { ...x, tags: next } : x)));
                      setPreviewDoc((d) => (d ? { ...d, tags: next } : d));
                    }}
                    className="opacity-0 group-hover:opacity-100 text-sky-400 hover:text-redline-600 transition-opacity"><X className="h-3 w-3" /></button>
                )}
              </span>
            ))}
            {!(previewDoc?.tags || []).length && <span className="text-xs text-ink-400 italic">No aliases yet — click Suggest to have the AI propose some.</span>}
          </div>

          <div className="flex items-baseline justify-between mt-1">
            <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Indexed text</div>
            {previewDoc?.content_source === "chunks" && previewDoc?.chunk_count > 0 && (
              <div data-testid="doc-preview-source-note" className="text-[11px] text-emerald-700 font-semibold">
                {previewDoc.chunk_count.toLocaleString()} chunks · {(previewDoc.indexed_chars || 0).toLocaleString()} chars — this is exactly what the concierge retrieves from
              </div>
            )}
            {previewDoc?.content_source === "extraction_preview" && (
              <div data-testid="doc-preview-source-note" className="text-[11px] text-amber-700 font-semibold">
                Extraction preview — no chunks yet ({previewDoc?.status || "processing"})
              </div>
            )}
          </div>
          <div data-testid="doc-preview-content" className="max-h-[45vh] overflow-y-auto rounded-xl bg-sand-50 border border-sand-200 p-4">
            {previewLoading ? (
              <div className="flex items-center justify-center py-10"><div className="h-6 w-6 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
            ) : (
              <p className="text-sm whitespace-pre-wrap leading-relaxed text-ink-800">{previewDoc?.content || "No readable text found."}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <MoveToCommunityDialog open={!!moveTarget} onClose={() => setMoveTarget(null)} tenants={tenants} currentId={tenantId} tenantId={tenantId}
        item={moveTarget?.item} kind={moveTarget?.kind} onMoved={() => { setPreviewDoc(null); load(); }} />
    </div>
  );
}

function TeachAssistant({ open, onOpenChange, suggestions, dismissed, readOnly, onResolve, onDismiss, onRestore, onDelete, onClearDismissed, onLink, onFetchRelated, library, canUpload, uploadFiles, queue, onDismissQueue }) {
  const [tab, setTab] = useState("open");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [q, setQ] = useState("");
  // iter195i: reset internal state whenever the flyer closes. Otherwise stale expandedId /
  // filter text hangs around and (in some interaction sequences) the debounced search inside
  // an expanded row keeps re-rendering AFTER the close — which was intermittently swallowing
  // the X button click ("type in search, then hit X, nothing happens"). Cleaning up on close
  // guarantees the next open is a fresh tree.
  useEffect(() => {
    if (!open) {
      setExpandedId(null);
      setQ("");
    }
  }, [open]);
  const list = tab === "open" ? suggestions : dismissed;
  const query = q.trim().toLowerCase();
  const filtered = query ? list.filter((s) => (s.question || "").toLowerCase().includes(query)) : list;
  const TabBtn = ({ id, label, count }) => (
    <button data-testid={`teach-tab-${id}`} onClick={() => { setTab(id); setExpandedId(null); }}
      className={`text-sm font-semibold rounded-full px-3.5 py-1.5 transition-colors flex items-center gap-1.5 ${tab === id ? "bg-ink text-white" : "text-ink-700 hover:bg-sand-100"}`}>
      {label}<span className={`text-[11px] rounded-full px-1.5 ${tab === id ? "bg-white/20" : "bg-sand-100 text-ink-700"}`}>{count}</span>
    </button>
  );
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="teach-assistant" className="w-full sm:max-w-lg p-0 flex flex-col gap-0"
        onPointerDownOutside={(e) => { if (e.detail?.originalEvent?.target?.closest?.("[data-sonner-toaster],[data-testid=upload-audience-dialog]")) e.preventDefault(); }}
        onInteractOutside={(e) => { if (e.detail?.originalEvent?.target?.closest?.("[data-sonner-toaster],[data-testid=upload-audience-dialog]")) e.preventDefault(); }}>
        <SheetHeader className="p-6 pb-4 border-b border-sand-100 space-y-1 text-left">
          <SheetTitle className="font-heading text-xl font-extrabold flex items-center gap-2">
            <span className="h-8 w-8 rounded-xl bg-amber-100 flex items-center justify-center"><Sparkles className="h-4 w-4 text-amber-600" strokeWidth={1.75} /></span>
            Teach your AI
          </SheetTitle>
          <SheetDescription>Answer the questions it couldn't, or add a document it should know. It learns instantly — no retraining.</SheetDescription>
        </SheetHeader>

        {canUpload && (
          <TeachUpload open={uploadOpen} onToggle={() => setUploadOpen((v) => !v)} uploadFiles={uploadFiles} queue={queue} onDismissQueue={onDismissQueue} />
        )}

        <div className="px-6 pt-4 flex items-center gap-2 flex-wrap">
          <TabBtn id="open" label="Needs answer" count={suggestions.length} />
          <TabBtn id="dismissed" label="Dismissed" count={dismissed.length} />
          {tab === "dismissed" && !readOnly && dismissed.length > 0 && (
            <button data-testid="teach-clear-dismissed"
              onClick={async () => { if (window.confirm(`Permanently delete all ${dismissed.length} dismissed question${dismissed.length === 1 ? "" : "s"}? This can't be undone.`)) { await onClearDismissed(); } }}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 rounded-full px-3 py-1.5 hover:bg-red-50 transition-colors">
              <Trash2 className="h-3.5 w-3.5" /> Clear all
            </button>
          )}
        </div>
        {list.length > 8 && (
          <div className="px-6 pt-3 relative">
            <Search className="h-4 w-4 text-ink-400 absolute left-9 top-1/2 -translate-y-1/2" />
            <input data-testid="teach-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions…"
              className="w-full rounded-full border border-sand-200 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500/30" />
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-2.5">
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-sand-200 bg-white px-4 py-12 text-center text-sm text-muted-foreground">
                {tab === "open" ? (query ? "No matching questions." : "All caught up — your AI has answers for everything so far.") : (query ? "No matching questions." : "Nothing dismissed.")}
              </div>
            ) : (
              filtered.map((s) => (
                <SuggestionRow key={s.id} s={s} readOnly={readOnly} tab={tab}
                  expanded={expandedId === s.id}
                  onToggle={() => setExpandedId((id) => (id === s.id ? null : s.id))}
                  onResolve={onResolve} onDismiss={onDismiss} onRestore={onRestore} onDelete={onDelete}
                  onLink={onLink} onFetchRelated={onFetchRelated} library={library} />
              ))
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Uploading from "Teach your AI" — the same pipeline as the Documents dropzone (chunked, asks who
// may see it, processes in the background). The warning is the point: managers reach for this
// when they wish the AI could DO something, and a document only teaches it what to SAY.
const TEACH_ACCEPT = ".pdf,.txt,.md,.csv,.docx,.xls,.xlsx,.pptx,.zip,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tif,.tiff";
function TeachUpload({ open, onToggle, uploadFiles, queue, onDismissQueue }) {
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const busy = (queue || []).some((q) => q.status === "uploading");
  return (
    <div className="px-6 pt-4" data-testid="teach-upload-block">
      <button type="button" onClick={onToggle} data-testid="teach-upload-toggle" aria-expanded={open}
        className="w-full flex items-center gap-2.5 rounded-2xl border border-sand-200 bg-white px-4 py-3 text-left hover:border-ink transition-colors">
        <span className="h-8 w-8 rounded-xl bg-redline-50 flex items-center justify-center shrink-0"><UploadCloud className="h-4 w-4 text-redline-500" strokeWidth={1.75} /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold text-ink">Add a document the AI should know</span>
          <span className="block text-xs text-muted-foreground">Rules, fees, hours, policies, minutes — anything {V.lcs("person")} ask about.</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-ink/40 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-2.5" data-testid="teach-upload-disclaimer">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" strokeWidth={2.25} />
            <div className="min-w-0">
              <div className="text-sm font-extrabold text-amber-900">This teaches the AI what to <u>say</u> — not what to <u>do</u>.</div>
              <p className="text-xs text-amber-900/85 mt-1 leading-relaxed">
                Upload {V.lc("org")} information {V.lcs("person")} ask about. If you upload a procedure — say, “how to prepare a resale certificate” — the AI will be able to <b>explain</b> the steps to whoever may see it. It will <b>not</b> start preparing certificates for you. Want Ask Redline to actually do a job like that? Tell the team with <b>Suggest a feature</b> in Ask Copilot.
              </p>
            </div>
          </div>
          <div data-testid="teach-upload-zone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            className={`rounded-2xl border-2 border-dashed px-4 py-4 flex items-center gap-3 cursor-pointer transition-colors ${dragging ? "border-redline-500 bg-redline-50" : "border-sand-200 hover:border-redline-400 hover:bg-redline-50/40"}`}>
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${dragging ? "bg-redline-500" : "bg-redline-50"}`}>
              {busy ? <Loader2 className={`h-5 w-5 animate-spin ${dragging ? "text-white" : "text-redline-500"}`} /> : <UploadCloud className={`h-5 w-5 ${dragging ? "text-white" : "text-redline-500"}`} strokeWidth={1.75} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading font-bold text-sm">{dragging ? "Drop to add" : "Drag & drop, or browse"}</div>
              <p className="text-xs text-muted-foreground">PDF, Word, Excel, PowerPoint, text, images, or a .zip of folders. You&apos;ll pick who may see it next.</p>
            </div>
            <span data-testid="teach-upload-browse" className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-xs font-semibold px-3.5 py-2">Browse</span>
            <input ref={fileRef} type="file" multiple accept={TEACH_ACCEPT} hidden data-testid="teach-upload-input" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
          </div>
          {(queue || []).length > 0 && (
            <div className="space-y-2" data-testid="teach-upload-queue">
              {queue.map((q) => (
                <div key={q.id} data-testid={`teach-upload-item-${q.id}`} className="flex items-center gap-3 rounded-xl border border-sand-200 bg-white px-3.5 py-2">
                  {q.status === "uploading" && <Loader2 className="h-4 w-4 animate-spin text-ink/40 shrink-0" />}
                  {q.status === "added" && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
                  {q.status === "error" && <AlertCircle className="h-4 w-4 text-danger-600 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">{q.name}</div>
                    <div className={`text-xs ${q.status === "error" ? "text-danger-600" : q.status === "added" ? "text-emerald-600 font-medium" : "text-muted-foreground"}`}>
                      {q.status === "uploading" && (q.pct != null ? `Uploading… ${q.pct}%` : "Uploading…")}
                      {q.status === "added" && "Added — processing in the background"}
                      {q.status === "error" && (q.error || "Failed")}
                    </div>
                  </div>
                  {q.status !== "uploading" && (
                    <button data-testid={`teach-upload-dismiss-${q.id}`} onClick={() => onDismissQueue(q.id)} className="shrink-0 h-8 w-8 inline-flex items-center justify-center text-ink/30 hover:text-ink"><X className="h-4 w-4" /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionRow({ s, readOnly, tab, expanded, onToggle, onResolve, onDismiss, onRestore, onDelete, onLink, onFetchRelated, library }) {
  const { tenantId } = useDash();
  // iter411 — "I know which document it is": browse the library by title and pick it outright,
  // instead of hoping the fuzzy auto-match surfaces the right one. The pick joins the match list
  // (flagged "You picked") so the same Attach / Draft / Use / Link actions apply.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const pickFromLibrary = (m) => {
    setPicked({ ...m, score: 1, picked: true });
    setRelated((r) => [{ ...m, score: 1, picked: true }, ...(r || []).filter((x) => x.id !== m.id)]);
    setPickerOpen(false);
  };
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(false); // texting the answer back is OPT-IN, never automatic
  // iter397 — taught answers carry audience chips like every other fact. Default follows the asker:
  // a registered resident asked → Residents; an unknown number asked → Anyone. Manager can change.
  const [audiences, setAudiences] = useState(() => ((s.askers || []).some((a) => a.name) ? ["residents"] : ["anyone"]));
  const [requireVerify, setRequireVerify] = useState(false);
  // Possible-match surfacing: lazily load related existing knowledge when the row expands
  // so managers can see "we already have an answer close to this" instead of retyping.
  const [related, setRelated] = useState(null); // null=not loaded, []=loaded no matches
  const [relatedLoading, setRelatedLoading] = useState(false);
  // iter195p — surface backend crash/timeout as a "retry" banner instead of the
  // misleading "No matches" state. Field bug: Cloudflare 520s on the RAG-heavy
  // /related endpoint were being caught silently and the manager thought their
  // fact library was empty.
  const [relatedError, setRelatedError] = useState(null);
  const [linkingId, setLinkingId] = useState("");
  // iter195f: manual search — when the auto-match misses, the manager types their own
  // query to find an existing quick fact/document/contact to link. Prevents the "just
  // type a fresh answer" doom-loop that duplicates existing entries.
  const [searchQ, setSearchQ] = useState("");
  useEffect(() => {
    if (!expanded || tab !== "open" || readOnly || related !== null || !onFetchRelated) return;
    let alive = true;
    setRelatedLoading(true);
    setRelatedError(null);
    onFetchRelated(s.id).then((r) => {
      if (!alive) return;
      // r may be either legacy `[]` shape or new `{items, error}` shape.
      const items = Array.isArray(r) ? r : (r?.items || []);
      const err = Array.isArray(r) ? null : (r?.error || null);
      setRelated(items);
      setRelatedError(err);
      setRelatedLoading(false);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, tab, readOnly, s.id]);
  // Debounced re-fetch when the manager types in the search box.
  useEffect(() => {
    if (!expanded || tab !== "open" || readOnly || !onFetchRelated) return;
    const q = searchQ.trim();
    if (!q) return; // empty search — keep auto-match results
    let alive = true;
    const timer = setTimeout(() => {
      setRelatedLoading(true);
      setRelatedError(null);
      onFetchRelated(s.id, q).then((r) => {
        if (!alive) return;
        const items = Array.isArray(r) ? r : (r?.items || []);
        const err = Array.isArray(r) ? null : (r?.error || null);
        setRelated(items);
        setRelatedError(err);
        setRelatedLoading(false);
      });
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ, expanded, tab, readOnly, s.id]);
  const retryFetch = () => {
    // Force the initial-load effect to re-run by clearing the loaded state.
    setRelated(null);
    setRelatedError(null);
  };
  const MIN = 15;
  const tooShort = answer.trim().length < MIN;
  const askers = s.askers || [];
  const fmtPhone = (p) => (p || "").replace(/^\+1(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3") || p;
  const askerLabel = (a) => (a.name || fmtPhone(a.phone)) + (a.channel === "voice" ? " · by call" : " · by text");
  // iter374 — "the answer IS this document": attach it, the concierge sends the link.
  const [attachedDoc, setAttachedDoc] = useState(null);
  const attachDoc = (m) => {
    setAttachedDoc({ id: m.id, title: m.title });
    if (!answer.trim() || answer.includes("[document link]")) {
      setAnswer(`Here's the ${m.title}: [document link]`);
    } else if (!answer.includes("[document link]")) {
      setAnswer(`${answer.trim()}\n[document link]`);
    }
    setDocNotes((n) => ({ ...n, [m.id]: "" }));
  };
  const save = async () => {
    if (tooShort) { toast.error(`Give a fuller answer (at least ${MIN} characters) so your concierge has something useful to say`); return; }
    setBusy(true);
    try { await onResolve(s.id, answer.trim(), notify, attachedDoc?.id, audiences, requireVerify); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); setBusy(false); }
  };
  const act = async (fn) => { setBusy(true); try { await fn(s.id); } catch { setBusy(false); } };
  const applyMatch = (m) => {
    // Prefill the answer box with the existing entry's answer so the manager can tweak & save
    // as a fresh entry (or click "Link as alias" instead to reuse without duplicating).
    if (m.answer) setAnswer(m.answer);
  };
  const linkMatch = async (m) => {
    if (!onLink) return;
    setLinkingId(m.id); setBusy(true);
    try { await onLink(s.id, m.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not link"); setLinkingId(""); setBusy(false); }
  };
  // iter373 — a document match gets a real action: read the document for this question.
  const [draftingId, setDraftingId] = useState("");
  const [docNotes, setDocNotes] = useState({});
  const draftFromDoc = async (m) => {
    setDraftingId(m.id);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/suggestions/${s.id}/draft-from-document`, { document_id: m.id });
      if (data.found) {
        setAnswer(data.answer);
        setDocNotes((n) => ({ ...n, [m.id]: "" }));
        toast.success(`Drafted from ${data.document} — check it, then save`);
      } else {
        setDocNotes((n) => ({ ...n, [m.id]: data.why || "This document doesn't contain the answer." }));
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't read that document"); }
    setDraftingId("");
  };
  const showMatchBadge = tab === "open" && !readOnly && (related?.length || 0) > 0 && !expanded;
  return (
    <div data-testid={`suggestion-${s.id}`} className={`rounded-2xl border transition-all ${expanded ? "border-amber-300 bg-amber-50/50 shadow-sm" : "border-sand-200 bg-white hover:border-amber-200 hover:shadow-sm"}`}>
      <div className="px-4 py-3.5">
        <div className="flex items-start gap-3">
          <span className="h-7 w-7 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5"><MessagesSquare className="h-3.5 w-3.5 text-amber-700" strokeWidth={2} /></span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-ink font-semibold leading-snug break-words">{s.question}</p>
            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
              {s.count > 1 && (
                <span className="inline-block text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">asked {s.count}×</span>
              )}
              {showMatchBadge && (
                <span data-testid={`suggestion-match-badge-${s.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  <Lightbulb className="h-3 w-3" strokeWidth={2} />
                  {related.length === 1 ? "Similar answer exists" : `${related.length} similar answers`}
                </span>
              )}
              {askers.length > 0 && (
                <span data-testid={`suggestion-askers-${s.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink/60 bg-sand-50 border border-sand-200 rounded-full px-2 py-0.5">
                  <UserCircle2 className="h-3 w-3" strokeWidth={2} />
                  Asked by {askers.slice(0, 2).map(askerLabel).join(", ")}{askers.length > 2 ? ` +${askers.length - 2} more` : ""}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="mt-2.5 flex items-center gap-1.5 justify-end">
          {readOnly ? (
            <span className="text-[11px] text-muted-foreground">a manager can answer this</span>
          ) : tab === "open" ? (
            <>
              <button data-testid={`suggestion-dismiss-${s.id}`} onClick={() => act(onDismiss)} disabled={busy} title="Dismiss"
                className="h-8 w-8 rounded-full flex items-center justify-center text-ink-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50">
                <X className="h-4 w-4" />
              </button>
              <button data-testid={`suggestion-answer-toggle-${s.id}`} onClick={onToggle}
                className="text-sm font-semibold rounded-full px-4 py-1.5 text-white bg-ink/90 hover:bg-ink transition-colors">{expanded ? "Close" : "Answer"}</button>
            </>
          ) : (
            <>
              <button data-testid={`suggestion-restore-${s.id}`} onClick={() => act(onRestore)} disabled={busy}
                className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full border border-sand-200 px-3.5 py-1.5 text-ink-700 hover:bg-sand-50 transition-colors disabled:opacity-50">
                <RotateCcw className="h-3.5 w-3.5" /> Restore
              </button>
              <button data-testid={`suggestion-delete-${s.id}`} onClick={() => act(onDelete)} disabled={busy}
                title="Delete permanently"
                className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full border border-red-200 px-3.5 py-1.5 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </>
          )}
        </div>
      </div>
      {expanded && !readOnly && tab === "open" && (
        <div className="px-4 pb-4">
          {/* iter195f: existing-knowledge browser ALWAYS renders when expanded — the manager
              needs to be able to search + link even when auto-match found zero, or they'll
              type a fresh answer and duplicate an entry they already have. */}
          <div data-testid={`suggestion-matches-${s.id}`} className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
              <div className="flex items-center gap-1.5 text-[12px] font-bold text-emerald-900 mb-2">
                <Lightbulb className="h-3.5 w-3.5" strokeWidth={2} />
                {relatedLoading ? "Checking your existing knowledge…"
                  : (relatedError ? "Couldn't load your library — retry below"
                  : ((related?.length || 0) === 0 ? (searchQ.trim() ? "No matches for your search — try different words, choose from the library, or type an answer below" : "No auto-matches — search, choose the document from your library, or type a fresh answer")
                  : (related.length === 1 ? "You may already have an answer for this"
                                          : "You may already have answers for this")))}
              </div>
              {/* iter195p — surface backend crash/timeout as an actionable banner. */}
              {relatedError && !relatedLoading && (
                <div data-testid={`suggestion-related-error-${s.id}`} className="mb-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" strokeWidth={2} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold leading-snug">Something went wrong loading your library</div>
                    <div className="text-[11px] opacity-80 mt-0.5 leading-snug break-words">{relatedError}</div>
                  </div>
                  <button data-testid={`suggestion-related-retry-${s.id}`} onClick={retryFetch}
                          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold rounded-full bg-amber-600 text-white px-2.5 py-1 hover:bg-amber-700 transition-colors">
                    <RefreshCw className="h-3 w-3" strokeWidth={2.5} /> Retry
                  </button>
                </div>
              )}
              {/* Manual search box — kicks a debounced re-fetch with the manager's query */}
              <div className="mb-2 flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink/40" strokeWidth={2} />
                  <input data-testid={`suggestion-search-${s.id}`} type="text" value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    placeholder="Search your quick facts, contacts, documents…"
                    className="w-full rounded-lg border border-emerald-200 bg-white pl-8 pr-3 py-1.5 text-[13px] outline-none focus:ring-2 focus:ring-emerald-500/30" />
                </div>
                <button type="button" data-testid={`suggestion-choose-library-${s.id}`} onClick={() => setPickerOpen((v) => !v)} aria-pressed={pickerOpen}
                  className={`shrink-0 inline-flex items-center gap-1 text-[12px] font-bold rounded-lg px-3 py-1.5 border transition-colors ${pickerOpen ? "bg-ink text-white border-ink" : "bg-white border-emerald-300 text-emerald-900 hover:border-ink"}`}
                  title="Know which document or fact answers this? Pick it by title.">
                  <FileText className="h-3.5 w-3.5" strokeWidth={2} /> Choose from library
                </button>
              </div>
              {pickerOpen && <LibraryPicker docs={library?.docs} entries={library?.entries} pickedId={picked?.id} onPick={pickFromLibrary} onClose={() => setPickerOpen(false)} />}
              <div className="space-y-2">
                {(related || []).map((m) => {
                  const pct = Math.max(30, Math.min(99, Math.round((m.score || 0) * 100)));
                  return (
                    <div key={m.id} data-testid={`match-${s.id}-${m.id}`}
                         className="rounded-lg border border-emerald-200 bg-white px-3 py-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-bold text-ink truncate">{m.title}</div>
                          <div className="text-[12px] text-ink/70 line-clamp-2 mt-0.5">{m.answer || m.snippet}</div>
                        </div>
                        <span data-testid={m.picked ? `match-picked-${s.id}-${m.id}` : undefined} className={`shrink-0 text-[10px] font-bold rounded-full px-1.5 py-0.5 border ${m.picked ? "text-ink bg-sand-100 border-sand-200" : "text-emerald-700 bg-emerald-100 border-emerald-200"}`}>
                          {m.picked ? "You picked" : `${pct}% match`}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        {m.source_type === "knowledge" && m.answer && (
                          <button data-testid={`match-use-${s.id}-${m.id}`} onClick={() => applyMatch(m)} disabled={busy}
                                  className="inline-flex items-center gap-1 text-[12px] font-semibold rounded-full border border-sand-200 px-3 py-1 text-ink hover:bg-sand-50 transition-colors disabled:opacity-50">
                            <Wand2 className="h-3 w-3" strokeWidth={2} /> Use this answer
                          </button>
                        )}
                        {m.source_type === "knowledge" && onLink && (
                          <button data-testid={`match-link-${s.id}-${m.id}`} onClick={() => linkMatch(m)} disabled={busy}
                                  className="inline-flex items-center gap-1 text-[12px] font-semibold rounded-full bg-emerald-600 text-white px-3 py-1 hover:bg-emerald-700 transition-colors disabled:opacity-50">
                            {linkingId === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" strokeWidth={2} />}
                            Link as alias
                          </button>
                        )}
                        {m.source_type === "document" && (
                          <>
                            <button data-testid={`match-attach-${s.id}-${m.id}`} onClick={() => attachDoc(m)} disabled={busy || attachedDoc?.id === m.id}
                                    className="inline-flex items-center gap-1 text-[12px] font-semibold rounded-full bg-emerald-600 text-white px-3 py-1 hover:bg-emerald-700 transition-colors disabled:opacity-50">
                              <Paperclip className="h-3 w-3" strokeWidth={2} /> {attachedDoc?.id === m.id ? "Attached" : "Send this document as the answer"}
                            </button>
                            <button data-testid={`match-draft-${s.id}-${m.id}`} onClick={() => draftFromDoc(m)} disabled={busy || draftingId === m.id}
                                    className="inline-flex items-center gap-1 text-[12px] font-semibold rounded-full border border-sand-300 text-ink/70 px-3 py-1 hover:border-ink/40 transition-colors disabled:opacity-50"
                                    title="Read this document and write a text answer from what it says">
                              {draftingId === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" strokeWidth={2} />}
                              {draftingId === m.id ? "Reading…" : "Write an answer from its text"}
                            </button>
                            <span className="text-[11px] font-semibold text-ink/60 inline-flex items-center gap-1 min-w-0">
                              <FileText className="h-3 w-3 shrink-0" strokeWidth={2} /> <span className="truncate">Document · {m.title}</span>
                            </span>
                          </>
                        )}
                      </div>
                      {docNotes[m.id] && (
                        <div data-testid={`match-draft-note-${s.id}-${m.id}`} className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] leading-snug text-amber-900">
                          {docNotes[m.id]}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="mt-2 text-[11px] text-ink/60 leading-snug" data-testid={`match-help-${s.id}`}>
                {(related || []).some((m) => m.source_type === "knowledge") && (
                  <><span className="font-semibold">Link as alias</span> teaches your concierge that this new phrasing means the same answer — no duplicate entry. <span className="font-semibold">Use this answer</span> fills the box below so you can tweak and save it as its own entry. </>
                )}
                {(related || []).some((m) => m.source_type === "document") && (
                  <>When the answer <span className="font-semibold">is</span> a document (a map, a form, the bylaws), tap <span className="font-semibold">Send this document as the answer</span> — the saved answer carries the file, your concierge texts the link, and it learns the {V.lc("person")}'s wording for that file. <span className="font-semibold">Write an answer from its text</span> reads the document and drafts a text answer instead (if it isn't in there, you'll be told).</>
                )}
              </p>
            </div>
          {attachedDoc && (
            <div data-testid={`suggestion-attached-doc-${s.id}`} className="mb-2 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
              <Paperclip className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
              <span className="min-w-0 flex-1 truncate"><span className="font-bold">Sends the document:</span> {attachedDoc.title} <span className="text-emerald-800/70">— the link goes where it says [document link]</span></span>
              <button data-testid={`suggestion-attached-doc-remove-${s.id}`} onClick={() => setAttachedDoc(null)} className="text-emerald-900/50 hover:text-red-600" aria-label="Remove document">×</button>
            </div>
          )}
          <textarea data-testid={`suggestion-answer-${s.id}`} rows={3} value={answer} disabled={busy} autoFocus
            onChange={(e) => setAnswer(e.target.value)}
            placeholder={`Type the answer your concierge should give ${V.lcs("person")}…`}
            className="w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500/30 disabled:opacity-60" />
          <div className="mt-2 rounded-xl bg-white border border-sand-200 px-3 py-2" data-testid={`suggestion-audience-${s.id}`}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-ink/45 mb-1.5">Who can the AI share this answer with?</div>
            <AudiencePicker tenantId={tenantId} testid={`suggestion-audience-${s.id}`} audiences={audiences} onAudiences={setAudiences}
              requireVerification={requireVerify} readOnly={busy} compact
              onRefuseOpen={() => toast.warning("Identity-checked answers can't be shared with \u201cAnyone\u201d \u2014 a texted code only proves phone possession, not membership.")} />
            <label data-testid={`suggestion-require-verify-${s.id}`} className="mt-2 flex items-center gap-2 text-xs text-ink/70 cursor-pointer">
              <input type="checkbox" checked={requireVerify} disabled={busy} className="h-3.5 w-3.5 accent-[#d97706]"
                onChange={(e) => { const on = e.target.checked; setRequireVerify(on); if (on && audiences.includes("anyone")) setAudiences(["residents"]); }} />
              <span><span className="font-semibold text-ink">Require identity check</span> — hidden until the caller passes the texted-code check (gate codes, {V.grp()} matters)</span>
            </label>
          </div>
          {askers.length > 0 && (
            <label data-testid={`suggestion-notify-${s.id}`} className="mt-2 flex items-start gap-2 rounded-xl bg-white border border-sand-200 px-3 py-2 cursor-pointer hover:border-amber-300 transition-colors">
              <input type="checkbox" checked={notify} disabled={busy} onChange={(e) => setNotify(e.target.checked)}
                className="h-4 w-4 mt-0.5 accent-[#d97706]" data-testid={`suggestion-notify-check-${s.id}`} />
              <span className="text-xs text-ink/70 leading-snug">
                <span className="font-bold text-ink">Also text this answer</span> to {askers.slice(0, 3).map((a) => a.name || fmtPhone(a.phone)).join(", ")}{askers.length > 3 ? ` +${askers.length - 3} more` : ""}
                <span className="block text-[11px] text-muted-foreground mt-0.5">Off by default — nothing is sent unless you tick this. Only askers who carry one of the chips above are texted{requireVerify ? "; identity-checked answers are never texted out" : ""}.</span>
              </span>
            </label>
          )}
          <div className="mt-2 flex items-center gap-2">
            <TextAssist value={answer} onChange={setAnswer} surface="knowledge" disabled={busy}
              testId={`suggestion-assist-${s.id}`} micTitle="Dictate answer" align="left" />
            <button data-testid={`suggestion-save-${s.id}`} onClick={save} disabled={busy || tooShort}
              className="inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink-700 transition-colors disabled:opacity-40">
              <Check className="h-4 w-4" /> Save answer
            </button>
            <span className={`text-[11px] ${tooShort && answer.length > 0 ? "text-amber-600" : "text-muted-foreground"}`}>
              {answer.trim().length < MIN ? `${MIN - answer.trim().length} more character${MIN - answer.trim().length === 1 ? "" : "s"} needed` : "Looks good"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function CardShell({ children, testid, onClick, dim }) {
  return (
    <div data-testid={testid} onClick={onClick}
      className={`relative bg-white rounded-3xl border border-sand-200 p-5 group transition-all duration-200 hover:-translate-y-1 hover:shadow-md ${onClick ? "cursor-pointer" : ""} ${dim ? "opacity-50 grayscale" : ""}`}>
      {children}
    </div>
  );
}

function DeleteBtn({ onDelete, testid }) {
  return (
    <span data-testid={testid} role="button" onClick={(e) => { e.stopPropagation(); onDelete(); }}
      className="absolute top-4 right-4 text-sand-300 hover:text-redline-500 transition-colors opacity-0 group-hover:opacity-100">
      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
    </span>
  );
}

function EditBtn({ onEdit, testid }) {
  return (
    <span data-testid={testid} role="button" onClick={(e) => { e.stopPropagation(); onEdit(); }}
      className="absolute top-4 right-11 text-sand-300 hover:text-ink transition-colors opacity-0 group-hover:opacity-100">
      <Pencil className="h-4 w-4" strokeWidth={1.5} />
    </span>
  );
}

function TaughtBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 text-[10px] font-bold">
      <Sparkles className="h-2.5 w-2.5" strokeWidth={2} /> Taught by AI
    </span>
  );
}

function TruncationBanner({ docs }) {
  // iter201 — surface documents whose ingest hit the pipeline's content cap
  // (2.5M chars / 300 OCR pages, iter200). No "Re-index" button here because
  // re-running under the same cap yields the same truncation — the actionable
  // fix is splitting the file into shorter volumes and re-uploading. Rendered
  // above every documents-tab view so a manager sees it the moment they land.
  const truncated = (docs || []).filter((d) => d.truncated);
  if (truncated.length === 0) return null;
  return (
    <div data-testid="truncation-banner" className="rounded-2xl border border-amber-300 bg-amber-50 p-5">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
          <Scissors className="h-4.5 w-4.5 text-amber-700" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-extrabold text-amber-900 text-base">
            {truncated.length === 1 ? "1 document is too large to fully index" : `${truncated.length} documents are too large to fully index`}
          </div>
          <p className="text-sm text-amber-800/90 mt-1 leading-relaxed">
            Your AI can quote from the first ~2.5&nbsp;million characters (roughly 300 pages of dense legal text) of any single document.
            The docs below exceed that limit, so their tail sections aren&apos;t searchable.
            <span className="font-semibold"> Split each one into shorter volumes</span> — for example a
            long {V.one("org_short")} Declaration becomes <em>Declaration</em>, <em>Bylaws</em>, <em>Rules</em>, and <em>Appendices</em> — then re-upload each part.
          </p>
          <div className="mt-3 space-y-1.5">
            {truncated.map((d) => {
              const lost = Math.max(0, (d.truncated_at_chars || 0) - 2_500_000);
              const lostPages = Math.round(lost / 3500);
              return (
                <div key={d.id} data-testid={`truncation-row-${d.id}`}
                  className="flex items-center justify-between gap-3 text-sm bg-white/60 rounded-lg px-3 py-2 border border-amber-200">
                  <div className="font-semibold text-ink truncate">{d.title}</div>
                  <div className="text-xs text-amber-800 tabular-nums shrink-0">
                    {d.truncated_at_chars ? `${d.truncated_at_chars.toLocaleString()} chars total` : "over limit"}
                    {lost > 0 && <span className="text-amber-700"> · ~{lostPages.toLocaleString()} pages beyond the limit not indexed</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function DocCard({ d, readOnly, onPreview, onDelete, onRetry }) {
  const queued = d.status === "queued";
  const processing = d.status === "processing";
  const failed = d.status === "failed";
  const stored = d.status === "stored";
  return (
    <CardShell testid={`doc-card-${d.id}`} onClick={processing || queued ? undefined : onPreview}>
      {!readOnly && <DeleteBtn onDelete={onDelete} testid={`doc-delete-${d.id}`} />}
      <div className="h-10 w-10 rounded-xl bg-redline-50 flex items-center justify-center">
        {processing ? <Loader2 className="h-5 w-5 text-redline-500 animate-spin" />
          : queued ? <Hourglass className="h-5 w-5 text-amber-500" strokeWidth={1.75} />
          : failed ? <AlertCircle className="h-5 w-5 text-danger-600" strokeWidth={1.75} />
          : <FileText className={`h-5 w-5 ${stored ? "text-amber-600" : "text-redline-600"}`} strokeWidth={1.5} />}
      </div>
      <div className="font-heading font-bold mt-3 pr-6">{d.title}</div>
      {processing ? (
        <div className="text-xs text-redline-600 mt-1 font-medium">Reading &amp; indexing…</div>
      ) : queued ? (
        <div data-testid={`doc-queued-${d.id}`} className="text-xs text-amber-600 mt-1 font-medium">Waiting in line{d.queue_position ? ` (#${d.queue_position})` : ""} — you&apos;ll be notified when it&apos;s ready</div>
      ) : failed ? (
        <>
          <div className="text-xs text-danger-600 mt-1">{d.error || "Couldn't process this file."}</div>
          {!readOnly && (
            <button data-testid={`doc-retry-${d.id}`} onClick={(e) => { e.stopPropagation(); onRetry?.(); }}
              className="mt-2 inline-flex items-center gap-1 rounded-full bg-ink text-white text-[11px] font-semibold px-3 py-1 hover:bg-ink-700">
              <RotateCcw className="h-3 w-3" /> Retry
            </button>
          )}
        </>
      ) : stored ? (
        <>
          <div data-testid={`doc-stored-${d.id}`} className="text-xs text-amber-700 mt-1 font-medium">Saved — not AI-readable</div>
          <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{d.text_note || "No readable text found."} It can still be downloaded and shared.</p>
          {!readOnly && (
            <button data-testid={`doc-retry-${d.id}`} onClick={(e) => { e.stopPropagation(); onRetry?.(); }}
              className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold px-3 py-1 hover:bg-amber-200">
              <RotateCcw className="h-3 w-3" /> Try reading it again
            </button>
          )}
        </>
      ) : (
        <>
          <div className="text-xs text-muted-foreground mt-1">{d.doc_type} · {d.chunk_count} chunks{d.word_count ? ` · ${d.word_count.toLocaleString()} words` : ""}</div>
          {d.truncated && (
            <div data-testid={`doc-truncated-chip-${d.id}`}
              className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold px-2 py-0.5">
              <Scissors className="h-3 w-3" /> Too large — split &amp; re-upload
            </div>
          )}
          {d.summary && <p data-testid={`doc-summary-${d.id}`} className="text-[13px] text-ink-600 mt-2 leading-relaxed line-clamp-2">{d.summary}</p>}
          <div className="text-[11px] text-redline-600 font-semibold mt-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"><Eye className="h-3 w-3" /> Preview</div>
        </>
      )}
    </CardShell>
  );
}

function ListRow({ it, readOnly, onOpen, onDelete, onRetry }) {
  const KINDS = kindsFactory();
  const isDoc = it._type === "document";
  const cfg = isDoc ? null : (KINDS[it.kind] || KINDS.fact);
  const Icon = isDoc ? FileText : cfg.icon;
  const queued = isDoc && it.status === "queued";
  const processing = isDoc && it.status === "processing";
  const failed = isDoc && it.status === "failed";
  const stored = isDoc && it.status === "stored";
  const typeLabel = isDoc ? (it.doc_type || "Document") : cfg.label;
  const sub = isDoc
    ? (processing ? "Reading & indexing…"
      : queued ? `Waiting in line${it.queue_position ? ` (#${it.queue_position})` : ""}…`
      : failed ? (it.error || "Failed")
      : stored ? "Saved — not AI-readable (download still works)"
      : (it.summary || `${it.chunk_count || 0} chunks indexed`))
    : (it.description || "");
  return (
    <div data-testid={`list-row-${it.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-sand-50 transition-colors group">
      <div className="h-9 w-9 rounded-lg bg-redline-50 flex items-center justify-center shrink-0">
        {processing ? <Loader2 className="h-4 w-4 text-redline-500 animate-spin" /> : queued ? <Hourglass className="h-4 w-4 text-amber-500" /> : failed ? <AlertCircle className="h-4 w-4 text-danger-600" /> : <Icon className={`h-4 w-4 ${stored ? "text-amber-600" : "text-redline-600"}`} strokeWidth={1.75} />}
      </div>
      <button onClick={onOpen} className="min-w-0 flex-1 text-left">
        {/* Title always gets its own line — tags can wrap beneath it. Previously the
            title + 3 tag chips shared one truncate-line and on mobile the tags
            crowded the title down to nothing (field bug: "tags overlap the title
            so you can't read it"). Tags shown inline only on ≥sm screens. */}
        <div className="text-sm font-semibold text-ink truncate">{it.title}</div>
        {isDoc && it.truncated && (
          <div data-testid={`list-truncated-chip-${it.id}`}
            className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0.5">
            <Scissors className="h-2.5 w-2.5" /> Too large — split &amp; re-upload
          </div>
        )}
        {(it.tags || []).length > 0 && (
          <div className="mt-0.5 flex items-center gap-1 flex-wrap" data-testid={`list-tags-${it.id}`}>
            {(it.tags || []).slice(0, 3).map((t) => <TagChip key={t} t={t} />)}
          </div>
        )}
        <div className={`text-xs truncate mt-0.5 ${failed ? "text-danger-600" : processing ? "text-redline-600" : queued || stored ? "text-amber-600" : "text-muted-foreground"}`}>{sub}</div>
      </button>
      {failed && !readOnly && (
        <button data-testid={`list-retry-${it.id}`} onClick={onRetry} className="shrink-0 rounded-full bg-ink text-white text-[11px] font-semibold px-3 py-1 hover:bg-ink-700 flex items-center gap-1"><RotateCcw className="h-3 w-3" /> Retry</button>
      )}
      <div className="hidden sm:block text-xs font-medium text-ink/50 shrink-0 w-16 text-right capitalize">{typeLabel}</div>
      <div className="hidden md:block text-xs text-muted-foreground shrink-0 w-24 text-right">{fmtDate(it.created_at)}</div>
      {!readOnly && <button onClick={onDelete} data-testid={`list-delete-${it.id}`} className="shrink-0 text-ink/30 hover:text-danger-600 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-4 w-4" /></button>}
    </div>
  );
}

function EntryCard({ e, readOnly, onEdit, onDelete, onArchive, onMove }) {
  const KINDS = kindsFactory();
  const cfg = KINDS[e.kind] || KINDS.fact;
  const taught = e.source === "teach_assistant";
  if (e.kind === "event") {
    const dp = e.date ? dateParts(e.date) : null;
    return (
      <CardShell testid={`kn-card-${e.id}`} dim={e.past}>
        {!readOnly && <EditBtn onEdit={onEdit} testid={`kn-edit-${e.id}`} />}
        {!readOnly && <DeleteBtn onDelete={onDelete} testid={`kn-delete-${e.id}`} />}
        <div className="flex gap-4">
          <div className="shrink-0 w-14 rounded-2xl bg-redline-50 text-center overflow-hidden border border-redline-100">
            <div className="bg-redline-500 text-white text-[10px] font-bold py-0.5">{dp?.mon || "—"}</div>
            <div className="text-2xl font-black text-redline-600 py-1">{dp?.day || "?"}</div>
          </div>
          <div className="min-w-0 pr-4">
            <div className="text-[10px] font-bold tracking-wide uppercase text-redline-600 flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Event{e.past ? " · past" : ""}</div>
            <div className="font-heading font-bold leading-tight mt-0.5">{e.title}</div>
            {(e.date || e.time) && <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(e.date)}{e.time ? ` · ${e.time}` : ""}</div>}
            {e.description && <div className="text-xs text-ink-700 mt-1.5 line-clamp-3">{e.description}</div>}
          </div>
        </div>
      </CardShell>
    );
  }
  return (
    <CardShell testid={`kn-card-${e.id}`} dim={e.expired}>
      {!readOnly && <EditBtn onEdit={onEdit} testid={`kn-edit-${e.id}`} />}
      {!readOnly && <DeleteBtn onDelete={onDelete} testid={`kn-delete-${e.id}`} />}
      <div className={`h-10 w-10 rounded-xl ${cfg.bg} flex items-center justify-center`}><cfg.icon className={`h-5 w-5 ${cfg.color}`} strokeWidth={1.75} /></div>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span className="text-[10px] font-bold tracking-wide uppercase text-muted-foreground">{cfg.label}</span>
        {taught && <TaughtBadge />}
        {e.expired && !e.archived && <span className="rounded-full bg-gray-100 text-gray-500 px-2 py-0.5 text-[10px] font-semibold">Expired</span>}
        {e.kind === "fact" && <ExpiryBadge item={e} />}
      </div>
      <div className="font-heading font-bold leading-tight mt-0.5 pr-6">{e.title}</div>
      {e.kind === "announcement" && e.date && <div className="text-xs text-amber-600 font-medium mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> In effect until {fmtDate(e.date)}</div>}
      {e.description && <div className="text-xs text-ink-700 mt-1.5 line-clamp-4 whitespace-pre-wrap">{e.description}</div>}
      {(e.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2" data-testid={`kn-tags-${e.id}`}>
          {e.tags.map((t) => <TagChip key={t} t={t} />)}
        </div>
      )}
      {!readOnly && (onArchive || onMove) && (
        <div className="mt-3 flex gap-1.5 flex-wrap">
          {onArchive && <button type="button" data-testid={`kn-archive-${e.id}`} onClick={onArchive} className="inline-flex items-center gap-1 rounded-full border border-sand-200 px-2.5 py-1 text-[11px] font-semibold text-ink/60 hover:border-ink hover:text-ink"><Archive className="h-3 w-3" /> Archive</button>}
          {onMove && <button type="button" data-testid={`kn-move-${e.id}`} onClick={onMove} className="inline-flex items-center gap-1 rounded-full border border-sand-200 px-2.5 py-1 text-[11px] font-semibold text-ink/60 hover:border-ink hover:text-ink"><ArrowRightLeft className="h-3 w-3" /> Move</button>}
        </div>
      )}
    </CardShell>
  );
}
